// Typed wrappers around the Directory endpoints. Types come from
// `./generated/schema.ts` (regenerated via `pnpm generate`); the runtime is
// the shared @my/platform/http transport so bearer token, X-App-Key,
// X-Idempotency-Key, and Result<T> error mapping all behave identically to
// every other call in the suite.
import { request } from '@my/platform/http';
import type {
  components,
  AccountUserRoleValue,
  AddressTypeValue,
  AppNameValue,
  AppRoleValue,
  BaseStatusValue,
  CrossingPreferenceValue,
  PaymentTermBasisValue,
  SiteTypeValue,
  TaxTypeValue,
} from './generated/schema';

// Re-export the enum value unions so app code can spell types like
// `AccountingSystemValue` directly without reaching into `/generated`.
export type {
  AccountingSystemValue,
  AccountUserRoleValue,
  AccountUserStatusValue,
  AddressTypeValue,
  AppNameValue,
  AppRoleValue,
  BaseStatusValue,
  CrossingPreferenceValue,
  EquipmentClassValue,
  InvoiceTypeValue,
  NominalTypeValue,
  PaymentTermBasisValue,
  SequenceFormatValue,
  SequenceTypeApiValue,
  SequenceTypeValue,
  SiteTypeValue,
  TaxTypeValue,
  VatRoundingMethodValue,
} from './generated/schema';

/** `/directory/api/v1/info/api-info` response shape. */
export type ApiInfo = components['schemas']['ApiInfoResponse'];

/**
 * Organisation read model. Flat shape mirroring the real `OrganisationDto`
 * — note `mainOfficeAddressId` is just a Guid pointer; the address itself
 * is fetched via `getAddress(id)`.
 *
 * <p>
 * `tmsOrderReferenceLabelsJson` is layered in here until the OpenAPI
 * schema regen catches up — the field exists on the BE
 * `OrganisationResponse` already (it's how the TMS order detail page
 * resolves the per-org names of `Order.Reference1..4` slots), but the
 * generated `schema.ts` is stale. Same pattern as the `Address`
 * `fullAddress` / `placeId` override above.
 * </p>
 */
/**
 * kg CO₂ per km, tank-to-wheel (Tier 1 · Scope 1) — the Sustainability section
 * on Central Configuration. The values in force (the organisation's own, or
 * the default where it set none), plus the defaults for comparison.
 */
export interface EmissionFactors {
  ladenDry: number;
  ladenTemperature: number;
  emptyDry: number;
  emptyTemperature: number;
  defaults: { ladenDry: number; ladenTemperature: number; emptyDry: number; emptyTemperature: number };
  /** Nothing differs from the defaults. */
  isDefault: boolean;
  updatedUtc: string | null;
  method: string;
}

/** PUT `/organisations/{id}/emission-factors`. A null factor follows the default. */
export interface SetEmissionFactorsRequest {
  ladenDry: number | null;
  ladenTemperature: number | null;
  emptyDry: number | null;
  emptyTemperature: number | null;
}

export type Organisation = components['schemas']['OrganisationResponse'] & {
  /** Written only by `PUT organisations/{id}/emission-factors`. */
  emissionFactors?: EmissionFactors;
  /**
   * JSON object string keyed by slot name (e.g.
   * `{ "Reference1": "Customer PO", "Reference2": "Internal Job" }`).
   * `null` / missing when the operator hasn't configured the labels yet
   * — TMS falls back to "Reference 1", "Reference 2", … in that case.
   */
  tmsOrderReferenceLabelsJson?: string | null;

  /**
   * JSON object string keyed by Work View tab name, value `true` to show
   * it (e.g. `{ "Orders": true, "Manifest": false }`). `null` / missing
   * when nobody has configured it — and a MISSING KEY means shown, so a
   * tab added in a later build is on for every org that saved before it
   * existed. Written only by Stratum support; see
   * `updateOrganisationTmsWorkViewTabs`.
   */
  tmsWorkViewTabsJson?: string | null;

  /**
   * JSON object string describing what the TMS Plan cockpit offers —
   * `{ "pools": { "subcontractors": false }, "gantt": true }`. Both
   * sections are optional and a MISSING KEY MEANS SHOWN, so a pool added
   * in a later build is on for every org that saved before it existed.
   * `null` when nobody has configured it. Written only by Stratum
   * support; see `updateOrganisationTmsPlanFeatures`.
   */
  tmsPlanFeaturesJson?: string | null;

  /**
   * JSON object string keyed by navigable-area name, value `true` to offer
   * it — `{ "roster": false, "invoicing": false }`. A MISSING KEY MEANS ON.
   *
   * <p>
   * Read this only to EDIT it. Screens must not branch on it: the same
   * fact narrows the capability list `GET /users/me` returns, and
   * `usePermissions().can(...)` is the one question every gate asks. Two
   * sources for one answer is how a menu and a route guard come to
   * disagree.
   * </p>
   */
  tmsMenuJson?: string | null;

  /**
   * JSON object string of TMS copy functions offered —
   * `{ "ordersCopySection": true }`. NOT "missing key means on": each setting
   * has its own default, held by the TMS app (the orders copy section's is
   * hidden while it's being built). `null` when nobody has configured it.
   * Written only by Stratum support; see `updateOrganisationTmsCopyFunctions`.
   */
  tmsCopyFunctionsJson?: string | null;

  /**
   * JSON object string saying whether the TMS ferry (shipping) integration is
   * on — `{ "enabled": true }`. Default off, held by the TMS app; `null` when
   * nobody has configured it. Written only by Stratum support; see
   * `updateOrganisationTmsShippingIntegration`.
   */
  tmsShippingIntegrationJson?: string | null;

  /**
   * JSON object string of Depot system settings, grouped by section —
   * `{ "products": { "restrictedWeightLimit": true } }`. Values are
   * booleans, numbers or text. OVERRIDES ONLY: a missing setting means
   * Depot's shipped default, and `null` means every setting as shipped.
   * Written through `updateOrganisationDepotSettings`.
   */
  depotSettingsJson?: string | null;
};

/** PUT `/organisations` body. */
export type UpdateOrganisationRequest = components['schemas']['UpdateOrganisationRequest'];

/**
 * POST `/organisations` body.
 *
 * <p>
 * `appKeys` MAY BE EMPTY, which creates a bare organisation. Passing apps seeds
 * the CALLER an Owner role in each, so it only makes sense when the caller is a
 * member of the account — My Hub sends none and lets the Subscriptions screen
 * enable apps afterwards, which grants the role to the account's owners.
 * </p>
 */
export interface CreateOrganisationRequest {
  name: string;
  invoicingName: string;
  taxNumber?: string | null;
  registrationNumber?: string | null;
  operationalTimeZone?: string;
  languageCode?: string;
  currencyIsoCode?: string;
  countryIsoCode?: string;
  timePattern?: string;
  datePattern?: string;
  appKeys: AppNameValue[];
}

/** PUT `/organisations/{id}/additional-currencies` body. */
export type UpdateOrganisationAdditionalCurrenciesRequest =
  components['schemas']['UpdateOrganisationAdditionalCurrenciesRequest'];

/**
 * PUT `/organisations/{id}/tms-order-reference-labels` body. The BE
 * validates that `labelsJson` is either `null` (clears the column) or
 * a well-formed JSON OBJECT — bare strings / arrays / primitives are
 * rejected with 422.
 */
export interface UpdateOrganisationTmsOrderReferenceLabelsRequest {
  labelsJson: string | null;
}

/**
 * PUT `/organisations/{id}/tms-work-view-tabs` body. The BE validates
 * that `tabsJson` is either `null` (clears the column, restoring every
 * tab) or a JSON OBJECT whose values are all booleans with at least one
 * `true` — an all-off map is rejected with 422, as is anything that is
 * not an object of booleans.
 *
 * <p>
 * SUPPORT ONLY. The endpoint answers 404 — not 403 — to a token without
 * the `super_user` claim, matching the rest of the support surface.
 * Send the COMPLETE map of the tabs this build knows about: the "at
 * least one enabled" check on the BE assumes it, and a sparse map could
 * not tell "switched off" from "a tab that did not exist yet".
 * </p>
 */
export interface UpdateOrganisationTmsWorkViewTabsRequest {
  tabsJson: string | null;
}

/**
 * PUT `/organisations/{id}/tms-plan-features` body. The BE validates
 * that `featuresJson` is either `null` (clears the column, restoring the
 * cockpit as shipped) or a JSON OBJECT with an optional `pools` object of
 * booleans and an optional `gantt` boolean. Unknown top-level keys are
 * ignored rather than rejected, so a client ahead of the service can add
 * a toggle without the write failing.
 *
 * <p>
 * SUPPORT ONLY — 404, not 403, for a token without the `super_user`
 * claim. Unlike the Work View tabs there is no "at least one" rule: every
 * pool off is a legitimate setup.
 * </p>
 */
export interface UpdateOrganisationTmsPlanFeaturesRequest {
  featuresJson: string | null;
}

/**
 * PUT `/organisations/{id}/tms-menu` body. `menuJson` is either `null`
 * (clears the column, restoring the whole menu) or a JSON OBJECT of
 * booleans. Unknown area names are accepted and simply match nothing.
 *
 * <p>
 * SUPPORT ONLY, like the two endpoints above — 404, not 403, for a token
 * without the `super_user` claim. It has the strongest claim to that of
 * the three: the others hide UI, this narrows what anybody in the
 * organisation can reach.
 * </p>
 */
export interface UpdateOrganisationTmsMenuRequest {
  menuJson: string | null;
}

/**
 * PUT `/organisations/{id}/tms-copy-functions` body. `functionsJson` is either
 * `null` (clears the column — every setting back to its default) or a JSON
 * OBJECT of booleans keyed by setting name.
 */
export interface UpdateOrganisationTmsCopyFunctionsRequest {
  functionsJson: string | null;
}

/**
 * PUT `/organisations/{id}/tms-shipping-integration` body. `settingsJson` is
 * either `null` (back to the default, off) or a JSON OBJECT of booleans.
 */
export interface UpdateOrganisationTmsShippingIntegrationRequest {
  settingsJson: string | null;
}

/**
 * PUT `/organisations/{id}/depot-settings` body. `settingsJson` is either
 * `null` (clears the column, restoring every setting as shipped) or a JSON
 * OBJECT of sections, each an object whose values are booleans, numbers or
 * text. Lists, nulls and deeper nesting are rejected with 422; the setting
 * names are not checked — they belong to Depot.
 *
 * <p>
 * Needs Organisations → Update (403 otherwise). Send only the settings that
 * differ from Depot's defaults.
 * </p>
 */
export interface UpdateOrganisationDepotSettingsRequest {
  settingsJson: string | null;
}

/** Address read model returned by `/addresses/{id}`. */
/**
 * Address read shape. Layered on top of the generated
 * `AddressResponse`: the structured `address1` / `address2` fields
 * have been collapsed into a single Google-formatted `fullAddress`
 * plus a `placeId` for re-fetch / dedup. We use `Omit` to drop the
 * stale fields and intersect the new ones until the schema is
 * regenerated against the post-migration BE. `partyName` / `partyType`
 * remain list-context extensions populated by the BE inline join.
 */
export type Address = Omit<
  components['schemas']['AddressResponse'],
  'address1' | 'address2'
> & {
  fullAddress: string;
  placeId: string;
  partyName?: string | null;
  partyType?: PartyTypeValue | null;
  /** Where the coordinates came from; null when there are none. */
  coordinatesSource?: CoordinatesSourceValue | null;
  /** When they were last set or fetched; null = unknown. */
  coordinatesUpdatedUtc?: string | null;
};

/**
 * Where an address's coordinates came from. `Google` (a Places pick or Find on
 * map) is refreshed from the place id before it turns 30 days old — Google's
 * caching limit; `Manual` (a pin the operator dragged or tapped, or our own
 * port list) is ours and never refreshed.
 */
export type CoordinatesSourceValue = 'Google' | 'Manual';

/** PUT `/addresses` body. */
/**
 * PUT `/addresses` body — a full-payload REPLACE.
 *
 * <p>
 * Every column the handler assigns is REQUIRED here, even the nullable ones,
 * because omitting one does not mean "leave it alone" — it means "set it to
 * null". A screen that edits part of an address (the party page's address
 * editor, say) has to echo the rest of the row back, and making the fields
 * required turns forgetting one into a compile error instead of silent data
 * loss. `serviceTags` was exactly that bug: the party address editor did not
 * render tags and did not send them, so saving an address from the party page
 * cleared the tags somebody set on the Locations screen.
 * </p>
 */
export type UpdateAddressRequest = Omit<
  components['schemas']['UpdateAddressRequest'],
  'address1' | 'address2'
> &
  Required<
    Pick<
      components['schemas']['UpdateAddressRequest'],
      'code' | 'city' | 'postcode' | 'latitude' | 'longitude' | 'serviceTags'
    >
  > & {
    fullAddress: string;
    placeId: string;
    /**
     * Required so no editor can forget it: a dragged pin saved without it would
     * be treated as Google and later refreshed back off the gate. Only read
     * when the coordinates moved; null lets Directory infer it.
     */
    coordinatesSource: CoordinatesSourceValue | null;
  };

/** POST `/addresses` body. */
export type CreateAddressRequest = Omit<
  components['schemas']['CreateAddressRequest'],
  'address1' | 'address2'
> & {
  fullAddress: string;
  placeId: string;
  /** Where the coordinates came from (see `UpdateAddressRequest`); null = infer. */
  coordinatesSource: CoordinatesSourceValue | null;
};

/**
 * POST `/addresses` body for port / terminal Location records auto-created
 * from the route catalogue when an operator picks a booking route.
 *
 * The BE validator waives the Google Places requirement (`fullAddress` /
 * `placeId`) for `Location` type addresses, so all geocoding fields below
 * are optional: when the booking form can resolve the carrier's terminal
 * via Google Places it sends `code` + coordinates + `fullAddress` +
 * `placeId`; when the lookup fails it falls back to a bare name + country
 * row (legacy behaviour) and the BE still accepts it.
 *
 * `code` is the per-carrier terminal code `"{carrierCode}-{portCode}"`
 * (e.g. `"IF-DUB"`) — distinct per carrier so two carriers serving the
 * same physical port each get their own Location row.
 */
export interface CreateLocationAddressRequest {
  organisationId: string;
  addressType: 'Location';
  name: string;
  /** `"{carrierCode}-{portCode}"`, e.g. `"IF-DUB"`. */
  code?: string | null;
  city?: string | null;
  postcode?: string | null;
  countryIsoCode: string;
  latitude?: number | null;
  longitude?: number | null;
  /** Google `formatted_address` when geocoded; omitted on the fallback path. */
  fullAddress?: string | null;
  /** Google `place_id` when geocoded; omitted on the fallback path. */
  placeId?: string | null;
  status?: BaseStatusValue;
}

/** POST `/sites` body. */
export type CreateSiteRequest = components['schemas']['CreateSiteRequest'];

/**
 * PUT `/sites` body. Hand-mirrored from the BE `UpdateSiteCommand`
 * (see `My.Directory.Application/Features/Sites/UpdateSite`) because
 * the current generated swagger snapshot pre-dates the endpoint being
 * exposed. Once the schema is regenerated this can collapse back to
 * `components['schemas']['UpdateSiteCommand']`.
 */
/**
 * PUT `/sites` body — a full REPLACE.
 *
 * <p>
 * Every field is required, and the server refuses a body that omits one. The
 * optional spelling here was the bug: the Sites tab (the only site editor, in
 * all five apps) sent five of the eight fields, so every save reset the site's
 * type to Site, its status to Active, and cleared its parent, capacity and
 * amenities. The GET now returns all of them, so a screen that edits the name
 * can echo the rest.
 * </p>
 */
export interface UpdateSiteRequest {
  siteId: string;
  name: string;
  type: SiteTypeValue;
  parentId: string | null;
  addressId: string;
  capacity: number;
  amenities: string | null;
  status: BaseStatusValue;
}

/** Tax-rate row returned from `GET /organisationTaxRates`. */
export type OrganisationTaxRate = components['schemas']['OrganisationTaxRateResponse'];

/** POST `/organisationTaxRates` body. */
export type CreateOrganisationTaxRateRequest =
  components['schemas']['CreateOrganisationTaxRateRequest'];

/** PUT `/organisationTaxRates` body. */
export type UpdateOrganisationTaxRateRequest =
  components['schemas']['UpdateOrganisationTaxRateRequest'];

/** POST `/organisationTaxRates/seed-defaults` body. */
export type SeedOrganisationTaxRatesRequest =
  components['schemas']['SeedOrganisationTaxRatesRequest'];

/** Which VAT rulebook the organisation invoices under — from its registered country. */
export type VatRegimeValue = 'Eu' | 'Uk';
/** Where the customer is registered for VAT, relative to the organisation. */
export type VatCustomerScopeValue = 'Home' | 'OtherEu' | 'Outside';
/** Where the goods go, relative to the organisation's VAT area. */
export type VatRouteScopeValue = 'Inside' | 'Outside' | 'Any';

/** One line of the VAT treatment matrix and the tax schema the organisation chose for it. */
export interface VatTreatmentLine {
  /** `EU-1` … `UK-3`. */
  ruleKey: string;
  customer: VatCustomerScopeValue;
  route: VatRouteScopeValue;
  /** What "Set defaults" maps the line to. */
  suggestedTaxType: TaxTypeValue;
  /** The organisation's tax schema; null = not set. */
  taxRateId: string | null;
}

/** `GET /organisationVatTreatments` — the matrix for one organisation. */
export interface VatTreatmentMatrix {
  /** Null when there is no matrix for the organisation's country yet (NO, CH, …). */
  regime: VatRegimeValue | null;
  homeCountryIsoCode: string;
  lines: VatTreatmentLine[];
}

/** PUT `/organisationVatTreatments` body — a null `taxRateId` clears the line. */
export interface SetOrganisationVatTreatmentRequest {
  organisationId: string;
  ruleKey: string;
  taxRateId: string | null;
}

/** Nominal-code row returned from `GET /organisationNominals`. */
export type OrganisationNominal = components['schemas']['OrganisationNominalResponse'];

/** POST `/organisationNominals` body. */
export type CreateOrganisationNominalRequest =
  components['schemas']['CreateOrganisationNominalRequest'];

/** PUT `/organisationNominals` body. */
export type UpdateOrganisationNominalRequest =
  components['schemas']['UpdateOrganisationNominalRequest'];

/**
 * Static equipment catalogue payload returned from `GET /equipmentCatalogue`.
 * Two-level shape: classes (Trailer, Rigid) → groups → types. The catalogue
 * is the same for every tenant; cache it long.
 */
export type EquipmentCatalogue = components['schemas']['EquipmentCatalogueResponse'];

/** One class entry inside `EquipmentCatalogue` (Trailer or Rigid). */
export type EquipmentCatalogueClass = components['schemas']['EquipmentCatalogueClassResponse'];

/** One group of related types within a class (e.g. "Reefer & Temp"). */
export type EquipmentCatalogueGroup = components['schemas']['EquipmentCatalogueGroupResponse'];

/** One picker-able catalogue type — what the user toggles in the dialog. */
export type EquipmentCatalogueType = components['schemas']['EquipmentCatalogueTypeResponse'];

/**
 * Per-org equipment-type selection row returned from
 * `GET /organisationEquipmentTypes`. The chip label is rendered by looking
 * up `catalogueKey` against `EquipmentCatalogue`.
 */
export type OrganisationEquipmentType =
  components['schemas']['OrganisationEquipmentTypeResponse'];

/** PUT `/organisationEquipmentTypes` body — replaces the selection for one class. */
export type ReplaceOrganisationEquipmentTypesRequest =
  components['schemas']['ReplaceOrganisationEquipmentTypesRequest'];

/**
 * Static carrier catalogue payload from `GET /carrierCatalogue`. Curated
 * server-side JSON — same for every tenant — so cache aggressively (24h).
 * Keys are append-only / never reused, so existing org selections never
 * dangle.
 */
export type CarrierCatalogue = components['schemas']['CarrierCatalogueResponse'];

/** One carrier in `CarrierCatalogue`. */
export type CarrierCatalogueEntry = components['schemas']['CarrierCatalogueEntryResponse'];

/**
 * Per-org carrier selection row from `GET /organisationCarriers`. The chip
 * label is rendered by looking up `catalogueKey` against the catalogue;
 * `partyId` points at the auto-created (or pre-existing) Supplier party.
 */
export type OrganisationCarrier = components['schemas']['OrganisationCarrierResponse'];

/** PUT `/organisationCarriers` body — replaces the org's full carrier set. */
export type ReplaceOrganisationCarriersRequest =
  components['schemas']['ReplaceOrganisationCarriersRequest'];

/**
 * Static shipping-routes catalogue from `GET /routeCatalogue`. Each entry
 * references its primary `carrierKey` so the FE can resolve the operator
 * label against `CarrierCatalogue` in the same render.
 */
export type RouteCatalogue = components['schemas']['RouteCatalogueResponse'];

/** One route in `RouteCatalogue`. */
export type RouteCatalogueEntry = components['schemas']['RouteCatalogueEntryResponse'];

/** Per-org route selection row from `GET /organisationRoutes`. */
export type OrganisationRoute = components['schemas']['OrganisationRouteResponse'];

/** PUT `/organisationRoutes` body — replaces the org's full route set. */
export type ReplaceOrganisationRoutesRequest =
  components['schemas']['ReplaceOrganisationRoutesRequest'];

/** Bank account row returned from `GET /organisationBankAccounts`. */
export type OrganisationBankAccount = components['schemas']['OrganisationBankAccountResponse'];

/**
 * A saved document layout. `layoutJson` is opaque here — the app's builder owns
 * its shape; see `migrateLayout` on the consuming side.
 */
export type OrganisationDocumentTemplate =
  components['schemas']['OrganisationDocumentTemplateResponse'];
export type CreateOrganisationDocumentTemplateRequest =
  components['schemas']['CreateOrganisationDocumentTemplateRequest'];
export type UpdateOrganisationDocumentTemplateRequest =
  components['schemas']['UpdateOrganisationDocumentTemplateRequest'];

/** POST `/organisationBankAccounts` body. */
export type CreateOrganisationBankAccountRequest =
  components['schemas']['CreateOrganisationBankAccountRequest'];

/** PUT `/organisationBankAccounts` body. */
export type UpdateOrganisationBankAccountRequest =
  components['schemas']['UpdateOrganisationBankAccountRequest'];

/**
 * One numbering sequence per organisation per `SequenceType` (Invoice,
 * CreditNote, …). Returned from GET `/organisationSequences?organisationId`.
 */
export type OrganisationSequence = components['schemas']['OrganisationSequenceResponse'];

/** POST `/organisationSequences` body. */
export type CreateOrganisationSequenceRequest =
  components['schemas']['CreateOrganisationSequenceRequest'];

/** PUT `/organisationSequences` body. */
export type UpdateOrganisationSequenceRequest =
  components['schemas']['UpdateOrganisationSequenceRequest'];

/** POST `/organisationSequences/consume` body. */
export type ConsumeOrganisationSequenceRequest =
  components['schemas']['ConsumeOrganisationSequenceRequest'];

/** Return shape from `POST /organisationSequences/consume`. */
export type ConsumeOrganisationSequenceResponse =
  components['schemas']['ConsumeOrganisationSequenceResponse'];

/**
 * Result returned from `POST /organisations/{id}/logo`. The bare blob name
 * (`logo`) is rarely needed by the UI — it's exposed so callers writing back
 * to a TanStack-Query cache can keep the read-model perfectly in sync. The
 * `logoUrl` is the signed URL you actually render.
 */
export type UploadOrganisationLogoResponse =
  components['schemas']['UploadOrganisationLogoResponse'];

/**
 * Account returned by `/directory/api/v1/accounts/available`. The BE-generated
 * schema is still called `TenantResponse` (legacy naming on the contract);
 * the FE-facing alias uses the canonical "account" vocabulary used everywhere
 * else in the codebase. When the BE schema is renamed, only the right-hand
 * side of this alias needs to update.
 */
export type Account = components['schemas']['TenantResponse'];

/**
 * Directory user, returned from `GET /users`. Carries per-account memberships
 * (`accounts`) — each with the user's account-level role and an `appRoles`
 * slice keyed by `(app, role, organisationId)` that the FE filters on.
 */
export type User = components['schemas']['UserResponse'];

/**
 * One customer account as the Stratum support picker lists it. Hand-written
 * rather than pulled from `components['schemas']` because the support routes
 * are not in the generated schema yet.
 *
 * <p>
 * Note what is NOT here: the caller's role or membership status in the
 * account. A support identity is a member of none of them — it reaches every
 * account through the `super_user` token claim, not through a row.
 * </p>
 */
export interface SupportAccount {
  id: string;
  name: string;
  domain: string;
  /** ISO timestamp the account was created. */
  createdUtc: string;
  /**
   * Every subscription, expired and disabled ones included — a lapsed row is
   * the answer to most of the questions that bring somebody to this screen.
   * Callers asking "what can they use right now" filter on `isActive`.
   */
  subscriptions: SupportSubscription[];
  /** Organisations to choose between once the account is picked. */
  organisations: SupportOrganisation[];
}

/** POST `/subscriptions/provision` body. */
export interface ProvisionAppRequest {
  appKey: AppNameValue;
  organisationId: string;
  /** Defaults to `Basic` server-side. */
  plan?: 'Trial' | 'Basic' | 'Professional' | 'Enterprise';
  /** ISO date; omit for no expiry. */
  expiresUtc?: string | null;
}

/** POST `/subscriptions/provision` response. */
export interface ProvisionAppResponse {
  subscriptionId: string;
  appKey: string;
  organisationId: string;
  subscriptionCreated: boolean;
  roleGranted: boolean;
}

/**
 * PUT `/accounts` body — edits whichever account `X-Account-Key` names, or the
 * caller's own.
 *
 * <p>
 * `domain` is accepted from everyone but only CHANGEABLE by Stratum support:
 * it is how an email resolves to an account, and moving it rewrites nothing
 * already issued. The server rejects a change from anybody else, so send the
 * current value unchanged rather than omitting it.
 * </p>
 */
export interface UpdateAccountRequest {
  name: string;
  domain: string;
}

/** POST `/accounts` body — staff onboarding a customer. */
export interface CreateAccountRequest {
  accountName: string;
  /** The owner is INVITED. There is deliberately no password field. */
  ownerEmail: string;
  ownerFirstName?: string;
  ownerLastName?: string;
  /**
   * The app to enable, and the app the owner is invited into. Never `'Hub'` —
   * the portal is universal, so subscribing to it means nothing.
   */
  appKey: AppNameValue;
  /** Defaults to a 14-day trial server-side. */
  plan?: 'Trial' | 'Basic' | 'Professional' | 'Enterprise';
  /**
   * Create the owner with a generated temporary password instead of an invite,
   * and mark their email verified.
   *
   * <p>
   * FOR TEST ACCOUNTS, whose address usually receives nothing — there is no
   * invite to click and no verification link either, and every app hard-gates
   * on `email_verified`. The password is generated server-side, returned once,
   * and stored nowhere.
   * </p>
   */
  issueTemporaryPassword?: boolean;
}

/** POST `/accounts` response. */
export interface CreateAccountResponse {
  accountId: string;
  /**
   * Present only when a temporary password was requested AND the owner's login
   * was newly created. Shown once; nothing stores it.
   */
  temporaryPassword: string | null;
  /**
   * The owner already had a login, so nothing was issued or changed — they sign
   * in with the password they already have.
   */
  ownerAlreadyExisted: boolean;
}

/** One app subscription on a `SupportAccount`. */
export interface SupportSubscription {
  id: string;
  appKey: AppNameValue;
  plan: 'Trial' | 'Basic' | 'Professional' | 'Enterprise';
  /** Switched on. Says nothing about whether it has expired. */
  enabled: boolean;
  startsUtc: string;
  /** Null for no expiry — the normal case outside trials. */
  expiresUtc: string | null;
  /** Enabled, started and not expired. Ask this rather than recomputing it. */
  isActive: boolean;
}

/** An organisation inside a `SupportAccount`. */
export interface SupportOrganisation {
  id: string;
  name: string;
  networkId: string;
  isDefault: boolean;
}

/** Per-account membership row embedded in `User.accounts`. */
export type AccountSummary = components['schemas']['AccountSummaryResponse'];

/** Per-org slot under an `AccountSummary`. */
export type OrganisationSummary = components['schemas']['OrganisationSummaryResponse'];

/**
 * A single `(app, role, organisationId)` grant. Carries the user's
 * functional-role assignments for that app:
 *   - `functionalRoles` — explicitly assigned, admin-editable in
 *     Settings → Users
 *   - `derivedFunctionalRoles` — read-only, computed at read-time on the
 *     BE (today: `Driver`, populated when a Driver-type Resource matches
 *     the user's email in this org). Effective capability = union of both.
 */
export type AppRoleSummary = components['schemas']['AppRoleSummaryResponse'];

/**
 * One row of the functional-role catalogue, returned by
 * `GET /users/functional-roles`. `key` is the stable PascalCase
 * identifier stored on `AppRoleSummary.functionalRoles`; the display
 * label comes from each app's i18n bundle.
 */
export type FunctionalRole = components['schemas']['FunctionalRoleResponse'];

/**
 * The signed-in user, as the calling app sees them.
 *
 * <p>
 * Hand-written rather than generated: the endpoint is newer than the last
 * client regeneration. A regen should produce the same shape.
 * </p>
 */
export interface Me {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  app: AppNameValue;
  /** Platform role in that app — the CEILING on what they can reach. */
  /**
   * `'SuperUser'` is layered on here until the OpenAPI schema regen catches
   * up — the BE `AppRole` enum has it, the generated `AppRoleValue` union does
   * not yet. Same pattern as the `AppNameValue` widening for `'Check'`.
   */
  role: AppRoleValue | 'SuperUser';
  accountRole: AccountUserRoleValue;
  organisationId: string;
  /**
   * Functional roles as stored, explicit and derived together — use this to
   * show what somebody IS (a Driver), not where they may go.
   */
  functionalRoles: string[];
  /**
   * WHAT TO RENDER: the role's ceiling narrowed by their own functional roles.
   * Menu items, routes and controls ask this list. Never recompute it from the
   * role on the client — the two would disagree the first time a preset
   * changes on the server.
   */
  capabilities: string[];
}

/** Team row with embedded members, returned by `GET /teams`. */
export type Team = components['schemas']['TeamResponse'];

/** Slim member projection embedded in `Team` + `SiteWithMembers`. */
export type TeamMemberSummary = components['schemas']['TeamMemberSummaryResponse'];

/** POST `/teams` body. */
export type CreateTeamRequest = components['schemas']['CreateTeamRequest'];

/** PUT `/teams` body. */
export type UpdateTeamRequest = components['schemas']['UpdateTeamRequest'];

/** Single user-site assignment, returned by `GET /users/{id}/sites`. */
export type UserSite = components['schemas']['UserSiteResponse'];

/** PUT body for replacing a user's full Site assignment list. */
export type ReplaceUserSitesRequest = components['schemas']['ReplaceUserSitesRequest'];

/** PUT body for replacing a user's team memberships in an (org, app) scope. */
export type ReplaceUserTeamsRequest = components['schemas']['ReplaceUserTeamsRequest'];

/** Site row with embedded members, returned by `GET /sites/with-members`. */
/**
 * Site row with its members.
 *
 * <p>
 * The intersection carries the columns the generated schema has not caught up
 * with yet — they were added to `SiteWithMembersDto` so the Sites tab can echo
 * everything `UpdateSite` replaces. Drop the `&` once the OpenAPI types are
 * regenerated against the running API.
 * </p>
 */
export type SiteWithMembers = components['schemas']['SiteWithMembersResponse'] & {
  type: SiteTypeValue;
  parentId: string | null;
  capacity: number;
  amenities: string | null;
  status: BaseStatusValue;
};

/** PUT body for replacing a site's full member list. */
export type ReplaceSiteMembersRequest = components['schemas']['ReplaceSiteMembersRequest'];

/**
 * A pending account-membership invitation belonging to the calling user.
 * Returned by `GET /accountUsers/me/pending` — drives the post-login
 * prompt that lets a freshly-invited user accept (or, via the standard
 * delete flow, decline). Carries `accountUserId` which is the row to
 * accept against — `AccountSummary.id` is the *account* id and can't be
 * used for that call.
 */
export type PendingInvitation = components['schemas']['PendingInvitationResponse'];

/** Per-app slot inside a `PendingInvitation`. */
export type PendingAppRole = components['schemas']['PendingAppRoleResponse'];

/**
 * `POST /users/me/resend-verification` response. `sent: false` is NOT an
 * error — it is the normal answer when the address turned out to be verified
 * already, or when the caller is inside the cooldown window.
 */
export interface ResendVerificationResponse {
  sent: boolean;
  message?: string | null;
  /** Seconds to wait before another send is accepted. */
  retryAfterSeconds: number;
}

/** POST `/users` body — invite/create a user with roles in one shot. */
export type CreateUserRequest = components['schemas']['CreateUserRequest'];

/** PUT `/users` body — update profile, platform role, functional roles. */
export type UpdateUserRequest = components['schemas']['UpdateUserRequest'];

/** Single organisation slot inside an account. */
export type AccountOrganisation = components['schemas']['TenantOrganisationDto'];

/** Per-app role inside an account. */
export type AccountAppRole = components['schemas']['TenantAppRoleDto'];

// ── Mobile users (Settings → Users & Access → Mobile Users tab) ──────────────
// Hand-shaped: these admin endpoints aren't in the generated OpenAPI schema yet.

/** Platform-identity status of a mobile (phone-authenticated) user. */
export type MobileUserStatusValue = 'Invited' | 'Active' | 'Suspended' | 'Deleted';

/** Descriptive field role for a mobile user. */
export type MobileUserRoleValue = 'Driver' | 'Mechanic' | 'Operative' | 'Supervisor' | 'Other';

/** Admin list row — `GET /mobile-users`. */
export interface MobileUserSummary {
  id: string;
  displayName: string;
  /** Null for username-only users — drivers who gave no phone number. */
  mobileNumberE164: string | null;
  /** Admin-issued login id, including the account prefix. Null for phone users. */
  username: string | null;
  status: MobileUserStatusValue;
  role: MobileUserRoleValue | null;
  identityVerified: boolean;
  lastSeenUtc: string | null;
  deviceCount: number;
  organisationId: string;
  appRole: string;
}

/** POST `/mobile-users/invite` body. */
export interface InviteMobileUserRequest {
  organisationId: string;
  mobileNumberE164: string;
  displayName: string;
  role?: MobileUserRoleValue | null;
  appKey?: AppNameValue;
  appRole?: AppRoleValue;
  /**
   * The Driver resource this person is, linked as the identity is created.
   * Optional — a mechanic or yard operative doing checks has no driver record.
   * Setting it here rather than later is what makes their first check
   * attributable.
   */
  resourceId?: string | null;
}

/** POST `/mobile-users/invite` response. */
export interface InviteMobileUserResponse {
  mobileUserId: string;
  status: string;
  maskedMobileNumber: string | null;
  /**
   * False when SMS is disabled or unconfigured — the invite exists but nothing
   * was sent, so the admin has to pass on the install link themselves. Worth
   * surfacing: otherwise an admin assumes the driver got a text.
   */
  inviteSmsSent: boolean;
}

// ────────────────────────────────────────────────────────────────────────────
// Username-based mobile users
//
// The second way a driver gets app access. Some will not hand over a mobile
// number, so instead of phone + OTP they get a username and password. Both
// land on the same MobileUser record — MobileNumberE164 and Username are each
// nullable — and Directory issues the same token either way.
// ────────────────────────────────────────────────────────────────────────────

/** GET `/mobile-users/username-available` response. */
export interface MobileUsernameAvailability {
  /** The full username including the account prefix, e.g. `ACME-JSMITH`. */
  username: string;
  isAvailable: boolean;
  /** Alternatives when the requested one is taken. */
  suggestions: string[];
}

/** POST `/mobile-users/provision-username` body. */
export interface ProvisionMobileUsernameRequest {
  organisationId: string;
  displayName: string;
  /**
   * The part AFTER the account code — send `JSMITH`, not `ACME-JSMITH`. Omit
   * it to have one derived from the display name.
   */
  username?: string | null;
  /** Omit to have a strong one generated. */
  password?: string | null;
  role?: MobileUserRoleValue | null;
  appKey?: AppNameValue;
  appRole?: AppRoleValue;
  /** As on the invite body: the Driver resource this person is, if they are one. */
  resourceId?: string | null;
}

/**
 * Returned ONCE. The password is never stored in readable form — Auth0 holds
 * the hash and Directory holds nothing — so if it is not captured here the
 * only route back is an admin reset.
 */
export interface ProvisionMobileUsernameResponse {
  mobileUserId: string;
  username: string;
  /** Null when `existingLogin` — the person keeps the password they have. */
  initialPassword: string | null;
  status: string;
  /**
   * The username was already a login in this account (the same person on
   * another app), so this app was added to it rather than a second login made.
   */
  existingLogin: boolean;
}

/**
 * Returned once, from an admin reset. Mobile users have no email, so there is
 * no self-service reset link — an admin issues a new password and hands it over.
 */
export interface ResetMobileUserPasswordResponse {
  mobileUserId: string;
  username: string;
  newPassword: string;
}

/** PUT `/mobile-users/{id}` body — edit profile (phone is the identity, not editable). */
export interface UpdateMobileUserRequest {
  displayName: string;
  role?: MobileUserRoleValue | null;
}

// ────────────────────────────────────────────────────────────────────────────
// Resources (fleet — drivers, trucks, rigids, trailers, other)
//
// TODO: regenerate the schema once the new BE is deployed and replace the
// hand-written types below with `components['schemas']['ResourceResponse']`,
// `components['schemas']['ResourceCertificationResponse']`, etc. The shapes
// match the BE DTOs exactly — the swap is mechanical (delete the local
// types, change the right-hand side of the alias).
// ────────────────────────────────────────────────────────────────────────────

/** Closed set of fleet resource types — drivers, vehicles, "other" (forklifts etc). */
export type ResourceTypeValue = 'Driver' | 'Truck' | 'Rigid' | 'Trailer' | 'Other';

/**
 * Closed set of certification kinds (matches `CertificationKey` enum on the BE).
 *
 * `Custom` is the catch-all for app-defined service-tag-style certs (TMS
 * service tags, Depot inspection items, etc). Custom rows additionally
 * carry a free-form `customLabel` and an owning `app` so each app's
 * Resources page can filter to its own slice.
 */
export type CertificationKeyValue =
  | 'Adr'
  | 'Cpc'
  | 'Mot'
  | 'Tachograph'
  | 'DriverCard'
  | 'Insurance'
  | 'FirstAid'
  | 'Hiab'
  | 'Forklift'
  | 'Psv'
  | 'Coshh'
  | 'OLicence'
  | 'RoadTax'
  | 'Custom';

/** What the system does on cert expiry (mirrors `TagComplianceAction` on the BE). */
export type TagComplianceActionValue = 'None' | 'Warn' | 'Stop';

/** Resource-level compliance roll-up dot (mirrors `ComplianceIndicator` on the BE). */
export type ComplianceIndicatorValue = 'Red' | 'Yellow' | 'Green';

/**
 * Cert row attached to a resource. `attachmentUrl` is a short-lived SAS
 * URL minted by the BE on read — don't persist it client-side; refetch
 * when needed. Null when no file has been uploaded for this cert.
 */
export interface ResourceCertification {
  id: string;
  resourceId: string;
  certificationKey: CertificationKeyValue;
  /**
   * Free-form label, set only when `certificationKey === 'Custom'`. The
   * FE renders this in place of the catalogue label for the chip / row
   * name. Null for predefined certs.
   */
  customLabel: string | null;
  /**
   * Owning app for `Custom` certs (TMS / Depot / Expenses). Null for
   * predefined certs — those are cross-app. Each app's Resources page
   * filters custom certs to its own value before render.
   */
  app: AppNameValue | null;
  expiryDate: string | null; // ISO date string ("2027-03-01")
  warnDays: number | null;
  complianceAction: TagComplianceActionValue;
  note: string | null;
  attachmentUrl: string | null;
  attachmentFileName: string | null;
  attachmentContentType: string | null;
  attachmentFileSizeBytes: number | null;
  attachmentVersion: number;
}

/**
 * Resource read model returned from the BE. The compliance fields
 * (`complianceIndicator`, `isCompliant`, `nextExpiryDate`) are
 * recomputed BE-side on every cert change — trust them for grid sort /
 * filter without re-deriving on the FE.
 */
export interface Resource {
  id: string;
  organisationId: string;
  type: ResourceTypeValue;
  name: string;
  displayName: string | null;
  mobileNumber: string | null;
  mobileNumberVerified: boolean;
  email: string | null;
  reference: string | null;
  organisationEquipmentTypeId: string | null;
  /**
   * Home depot — a Directory Site id, or null when unassigned. The single
   * source of truth for where a resource is based (drivers included); the FE
   * resolves the name from its own sites list.
   */
  homeSiteId: string | null;
  /**
   * Driver's default vehicle — the Resource id of a Truck / Rigid, or null.
   * Drivers only. Current state, not history: which vehicle ran a given job
   * belongs on the Load alongside its truck/trailer snapshot.
   */
  defaultVehicleResourceId: string | null;
  /**
   * The mobile identity this driver signs in with, or null when they have not
   * been given the app. Drivers only — a vehicle is the subject of a check,
   * never the performer.
   */
  mobileUserId: string | null;
  status: BaseStatusValue;
  complianceIndicator: ComplianceIndicatorValue;
  isCompliant: boolean;
  nextExpiryDate: string | null;
  createdUtc: string;
  updatedUtc: string | null;
  certifications: ResourceCertification[];
}

/** POST `/directory/api/v1/Resources` body. */
export interface CreateResourceRequest {
  organisationId: string;
  type: ResourceTypeValue;
  name: string;
  displayName?: string | null;
  mobileNumber?: string | null;
  email?: string | null;
  reference?: string | null;
  organisationEquipmentTypeId?: string | null;
  /** Home depot Site id, or null for unassigned. Valid for every resource type. */
  homeSiteId?: string | null;
  /** Driver's default vehicle (a Truck / Rigid resource id). Drivers only. */
  defaultVehicleResourceId?: string | null;
  status?: BaseStatusValue;
}

/** PUT `/directory/api/v1/Resources` body. */
export interface UpdateResourceRequest {
  id: string;
  type: ResourceTypeValue; // included so the BE validator's type-conditional rules apply
  name: string;
  displayName?: string | null;
  mobileNumber?: string | null;
  email?: string | null;
  reference?: string | null;
  organisationEquipmentTypeId?: string | null;
  /** Home depot Site id, or null for unassigned. Valid for every resource type. */
  homeSiteId?: string | null;
  /** Driver's default vehicle (a Truck / Rigid resource id). Drivers only. */
  defaultVehicleResourceId?: string | null;
  status?: BaseStatusValue;
}

/**
 * PUT `/directory/api/v1/resources/{id}/mobile-user` body. `mobileUserId: null`
 * clears the link and leaves the identity itself alone.
 */
export interface LinkResourceMobileUserRequest {
  resourceId: string;
  organisationId: string;
  mobileUserId: string | null;
}

/**
 * PUT `/directory/api/v1/Resources/homeSite` body — assigns one depot to many
 * resources. `homeSiteId: null` clears it. The BE caps a batch at 1000 ids and
 * rejects the whole call if any id isn't in the organisation.
 */
export interface AssignResourcesHomeSiteRequest {
  organisationId: string;
  resourceIds: string[];
  homeSiteId: string | null;
}

/**
 * PUT `/directory/api/v1/Resources/certifications` body — upserts a
 * single cert. For `certificationKey === 'Custom'`, both `customLabel`
 * and `app` are required (BE validator enforces, FE should mirror so
 * the form is rejected client-side too). For predefined keys, both
 * MUST be omitted / null — the BE validator rejects accidental sends.
 */
export interface SetResourceCertificationRequest {
  resourceId: string;
  certificationKey: CertificationKeyValue;
  customLabel?: string | null;
  app?: AppNameValue | null;
  expiryDate?: string | null;
  warnDays?: number | null;
  complianceAction?: TagComplianceActionValue;
  note?: string | null;
}

/**
 * One entry in the certification catalogue (per-key metadata layer
 * sitting alongside the closed `CertificationKeyValue` enum).
 * `appliesTo` lists lower-case `ResourceTypeValue` strings;
 * `countries` lists ISO 3166-1 alpha-2 codes (+ `"*"` sentinel for
 * "applies everywhere").
 */
export interface CertificationCatalogueEntry {
  key: string; // lower_snake_case form ("adr", "driver_card") matching CertificationKeyValue's PascalCase
  label: string;
  description: string;
  appliesTo: string[]; // lower-case ResourceTypeValue ("driver", "truck", ...)
  countries: string[]; // ISO 3166-1 alpha-2 + "*"
}

/** Bundled cert catalogue payload — same shape as the BE's `CertificationCatalogue` value object. */
export interface CertificationCatalogue {
  version: number;
  certifications: CertificationCatalogueEntry[];
}

// ────────────────────────────────────────────────────────────────────────────
// Parties (customers + suppliers)
//
// Same hand-written-types-until-schema-regenerated convention as Resources
// above. Field names + nullability mirror the BE PartyDto / PartyContactDto /
// PartyDocumentDto exactly.
// ────────────────────────────────────────────────────────────────────────────
// Audit trail
// ────────────────────────────────────────────────────────────────────────────

/** What happened to a record. */
/** `Overridden` is written by TMS only (invoice-line hand changes); shared so one audit page can filter both. */
export type AuditActionValue = 'Created' | 'Updated' | 'Deleted' | 'Restored' | 'Overridden';

/**
 * One entry in a record's history.
 *
 * Entries produced by the same save share a `changeSetId` — one click on Save is
 * one thing that happened, even when it touched five tables, and the timeline
 * groups on it.
 */
export interface AuditLogEntry {
  id: string;
  changeSetId: string;
  occurredUtc: string;
  /** Email, `svc:{client}` for an integration, or "system". */
  actorKey: string;
  /** The identity provider's subject; null for machine tokens. */
  actorId: string | null;
  /** Which record it belongs to — the Audit Log screen shows every kind at once. */
  aggregateType: string;
  aggregateId: string;
  entityType: string;
  entityId: string;
  action: AuditActionValue;
  /**
   * The fields that moved, as `{"Status":{"from":"Draft","to":"Open"}}`, or the
   * whole row under `to` / `from` for a create or a delete. Raw on purpose — the
   * client renders it, and a server-side shape would have to guess how every
   * field should read.
   */
  changesJson: string | null;
}

// ────────────────────────────────────────────────────────────────────────────

/**
 * Closed set of party kinds. `Both` is for parties that are simultaneously
 * a customer AND a supplier — they show on both list pages and the BE
 * filter (`?type=Customer`) returns them in either context.
 */
export type PartyTypeValue = 'Customer' | 'Supplier' | 'Both';

/**
 * Sub-classification for supplier parties. Null on Customer / Both parties.
 * `ShippingCompany` vs `ShippingAgency` distinguishes the operator of a
 * vessel (Stena Line, Irish Ferries) from the agency that resells crossings
 * on behalf of customers (e.g. Victor Tracy) — both can be the invoicing
 * counterpart on an order's ferry booking depending on who the customer
 * booked through.
 */
export type SupplierTypeValue =
  | 'Generic'
  | 'ShippingCompany'
  | 'ShippingAgency'
  | 'Haulier'
  | 'Fuel'
  | 'Garage'
  | 'Warehouse'
  | 'Other';

/** Lifecycle status for a party. `CeasedTrading` is terminal-but-undeleted. */
export type PartyStatusValue = 'Active' | 'Suspended' | 'CeasedTrading';

/** Invoice cadence for a party — drives downstream billing-run scheduling. */
export type BillingFrequencyValue = 'Weekly' | 'Monthly' | 'Quarterly' | 'Yearly';

/** Default settlement method on invoices issued to (or received from) the party. */
/**
 * How an account is billed — effectively "what happens at invoicing time":
 *
 *  - `Cash`     prepay, settled before or on despatch rather than on terms
 *  - `Credit`   invoiced on the party's agreed payment terms
 *  - `SelfBill` the customer raises the invoice on our behalf and sends it for
 *               reconciliation; we issue no document of our own
 *  - `Internal` part of our own group, never invoiced
 *
 * ('Other' was retired — it carried no meaning, so nothing could act on it or
 * report against it.)
 */
export type PaymentMethodValue = 'Cash' | 'Credit' | 'SelfBill' | 'Internal';

/**
 * Role/kind of a contact attached to a party. `Other` (= 0) is the BE's
 * fallback for unknown/legacy contact rows. The order in this union matches
 * the BE enum; the FE display order is set independently in i18n keys.
 */
export type ContactTypeValue =
  | 'Other'
  | 'Primary'
  | 'Secondary'
  | 'Billing'
  | 'Shipping'
  | 'Dispatcher'
  | 'Owner'
  | 'Admin'
  | 'ItTech';

/**
 * Network-link state for a party against another tenant on the platform.
 * `NotInvited` is the default for self-managed parties; the rest of the
 * lifecycle is driven by the (deferred) invitations subsystem.
 */
export type InvitationStatusValue =
  | 'NotInvited'
  | 'Pending'
  | 'Accepted'
  | 'Expired'
  | 'Revoked'
  | 'Rejected';

/** Document classification on a party document upload. */
export type PartyDocumentTypeValue =
  | 'Contract'
  | 'Finance'
  | 'Identity'
  | 'Insurance'
  | 'Tax'
  | 'Other';

/**
 * Party read model returned from the BE. The address fields are GUID
 * pointers — the addresses themselves are loaded via `getAddress(id)` or
 * the dedicated party-addresses endpoint (wave 3).
 *
 * `extraFields` is a JSON string (operator-defined custom fields, parsed
 * client-side when rendered). Surfaced unparsed here so the wire shape
 * matches the BE DTO exactly; consumers that don't care about extras can
 * ignore the field.
 */
export interface Party {
  id: string;
  organisationId: string;
  code: string;
  name: string;
  type: PartyTypeValue;
  /** Supplier sub-classification — null on Customer / Both parties. */
  subtype: SupplierTypeValue | null;
  status: PartyStatusValue;
  primaryAddressId: string;
  billingName: string;
  billingAddressId: string;
  billingFrequency: BillingFrequencyValue;
  paymentMethod: PaymentMethodValue;
  creditLimit: number;
  /**
   * Agreed payment terms. **Null = inherit the organisation's default**
   * (`defaultPaymentTermDays` / `defaultPaymentTermBasis`) — the two fields
   * always move together. Resolve with `resolvePaymentTerms`.
   */
  paymentTermDays: number | null;
  paymentTermBasis: PaymentTermBasisValue | null;
  /**
   * Which document layouts this customer's paperwork prints with, as
   * `{ app: { documentKind: layoutKey } }` JSON — e.g.
   * `{"Tms":{"Invoice":"…","CreditNote":"…"}}`. Null, or a missing entry, means
   * **use the organisation's default** for that app and kind.
   *
   * Keyed by app because a layout belongs to one (layouts are scoped
   * organisation + app + kind), so a customer invoiced by two apps has a choice
   * per app. The values are layout KEYS, not foreign keys — a saved layout's id
   * or the fixed key of one the app ships ("stock-invoice-standard") — and are
   * resolved leniently: a key matching nothing in the printing app's list means
   * "fall back to the default", not "something is broken".
   *
   * Read and write it through `utils/documentLayoutPrefs`, never by hand: a
   * rewrite that does not merge would wipe the other apps' branches.
   */
  preferredDocumentLayoutsJson: string | null;
  /**
   * Order reference slots THIS customer insists on, as a JSON array of 1-based
   * slot numbers — `[1,3]`. Null or `[]` means nothing beyond whatever the
   * organisation's workflow already asks of every order.
   *
   * Slot NUMBERS, not labels: the names live in the organisation's
   * `tmsOrderReferenceLabelsJson` and may be renamed at any time, so a stored
   * label would quietly stop matching. Parse and build it with the TMS app's
   * `utils/orderReferenceLabels` helpers.
   *
   * ADVISORY — the TMS order screen marks these slots and warns when they are
   * blank; nothing refuses a write over them. Hard gates live in the workflow
   * policy, which applies to every customer alike. Written via
   * `setPartyRequiredOrderReferences`, never `updateParty`: that PUT is a full
   * replace and the Depot party screen does not send this TMS-only field.
   */
  tmsRequiredOrderReferencesJson: string | null;
  taxNumber: string | null;
  taxSchemeCountryCode: string;
  euVatStatement: boolean;
  currencyIsoCode: string;
  networkId: string | null;
  /**
   * Concurrency ceiling when this party is used as a subcontractor — jobs it may
   * hold at once. Null = no ceiling set, which is deliberately distinct from a
   * low one: the planning cockpit falls back to its own display default. Written
   * via `setPartyAssignmentCap`, not `updateParty`.
   */
  maxConcurrentAssignments: number | null;
  accountOwnerId: string | null;
  accountSystemName: string | null;
  accountSystemCode: string | null;
  extraFields: string | null;
  /**
   * Comma-joined ServiceTag names this party is tagged with. Null /
   * empty = no tags. Parsed by the FE on render with a split-on-comma
   * helper; the BE canonicalises (trim each entry, dedupe case-
   * insensitively) on every save so the wire shape is always tidy.
   */
  tags: string | null;
  /**
   * Primary-address summary fields surfaced inline so the parties list
   * grid can render Address / Country columns without an extra fetch
   * per row. Populated server-side from `Party.PrimaryAddress`; null
   * when the linked address is missing or the field itself is empty.
   * `primaryFullAddress` carries the Google-formatted single line that
   * replaced the legacy Address1/Address2 split.
   */
  primaryFullAddress: string | null;
  /**
   * Just the town / city. Surfaced alongside the full line for compact
   * surfaces that have room for a place name but not a whole formatted
   * address — they show this and keep `primaryFullAddress` for the tooltip.
   */
  primaryAddressCity: string | null;
  primaryAddressPostcode: string | null;
  primaryAddressCountryIsoCode: string | null;
  /**
   * Display name of the party's primary contact (preferring rows with
   * `ContactType.Primary`, otherwise the first non-deleted contact).
   * Null when the party has no contacts at all.
   */
  primaryContactName: string | null;
  linkedAccountId: string | null;
  linkedOrganisationId: string | null;
  linkedPartyId: string | null;
  invitationStatus: InvitationStatusValue;
  createdUtc: string;
  updatedUtc: string | null;
}

/**
 * `TrailerPresence` — why a subcontractor's trailer is on our yard, which decides
 * what happens to it after the job it was given.
 *
 * `Drop` is a single job and then the unit is spent; `Hire` returns to the pool
 * free after each job, until the hire period ends.
 */
export type TrailerPresenceValue = 'Drop' | 'Hire';

/**
 * A trailer a subcontractor has left with us, planned alongside the owned fleet.
 * Not a `Resource` — it belongs to the haulier, carries no compliance state, and
 * has its own lifecycle.
 *
 * <p>
 * Carries no reference to the job it's working: that link belongs on the
 * consuming side once assignment exists, so "is it free?" stays a query.
 * </p>
 */
export interface SubcontractorTrailer {
  id: string;
  organisationId: string;
  subcontractorPartyId: string;
  /** Supplying haulier's name, inlined so a card needs no extra fetch. */
  subcontractorName: string | null;
  /** Unit name or registration. */
  unitName: string;
  presence: TrailerPresenceValue;
  /** `yyyy-MM-dd`. Set on Hire, null on Drop. */
  onHireFrom: string | null;
  /** `yyyy-MM-dd`. Null = open-ended hire. */
  onHireTo: string | null;
  organisationEquipmentTypeId: string | null;
  /** Equipment type's catalogue key — resolve to a label via the equipment catalogue. */
  equipmentCatalogueKey: string | null;
  createdUtc: string;
  updatedUtc: string | null;
}

/** POST `/directory/api/v1/subcontractorTrailers` body. */
export interface CreateSubcontractorTrailerRequest {
  organisationId: string;
  subcontractorPartyId: string;
  unitName: string;
  presence: TrailerPresenceValue;
  /** Required when `presence` is `Hire`; must be null on `Drop`. */
  onHireFrom?: string | null;
  onHireTo?: string | null;
  organisationEquipmentTypeId?: string | null;
}

/** PUT `/directory/api/v1/subcontractorTrailers` body. `organisationId` is immutable. */
export interface UpdateSubcontractorTrailerRequest {
  trailerId: string;
  subcontractorPartyId: string;
  unitName: string;
  presence: TrailerPresenceValue;
  onHireFrom?: string | null;
  onHireTo?: string | null;
  organisationEquipmentTypeId?: string | null;
}

/** Contact row attached to a party (separate aggregate, separate endpoints). */
export interface PartyContact {
  id: string;
  organisationId: string;
  partyId: string;
  name: string;
  phoneNumber: string;
  email: string;
  notes: string | null;
  type: ContactTypeValue;
  createdUtc: string;
  updatedUtc: string | null;
}

/**
 * Document row attached to a party. `downloadUrl` is a short-lived SAS
 * URL minted by the BE on read — same lifecycle convention as cert
 * attachments. Don't persist; refetch when needed.
 */
export interface PartyDocument {
  id: string;
  organisationId: string;
  partyId: string;
  fileName: string;
  contentType: string;
  fileSizeBytes: number;
  contentHash: string | null;
  blobName: string;
  containerName: string;
  description: string | null;
  type: PartyDocumentTypeValue;
  downloadUrl: string | null;
  createdUtc: string;
  updatedUtc: string | null;
}

/** POST `/directory/api/v1/parties` body. */
export interface CreatePartyRequest {
  organisationId: string;
  code: string;
  name: string;
  type: PartyTypeValue;
  /** Supplier sub-classification — only meaningful when `type=Supplier`. */
  subtype?: SupplierTypeValue | null;
  status?: PartyStatusValue;
  primaryAddressId: string;
  billingName: string;
  billingAddressId: string;
  billingFrequency?: BillingFrequencyValue;
  paymentMethod?: PaymentMethodValue;
  creditLimit?: number;
  /** Null clears any customer-specific terms and falls back to the org default. */
  paymentTermDays?: number | null;
  paymentTermBasis?: PaymentTermBasisValue | null;
  /**
   * `{ app: { documentKind: layoutKey } }` JSON. Null clears every layout
   * preference. Build it with `utils/documentLayoutPrefs`, which merges rather
   * than replaces — other apps' choices live in the same column.
   */
  preferredDocumentLayoutsJson?: string | null;
  taxNumber?: string | null;
  taxSchemeCountryCode?: string;
  euVatStatement?: boolean;
  currencyIsoCode?: string;
  accountOwnerId?: string | null;
  accountSystemName?: string | null;
  accountSystemCode?: string | null;
  extraFields?: string | null;
  /** Comma-joined ServiceTag names. Null / empty = no tags. */
  tags?: string | null;
}

/**
 * PUT `/directory/api/v1/parties` body — a full-payload REPLACE.
 *
 * <p>
 * Every field is REQUIRED, including the nullable ones, and that is deliberate:
 * `UpdatePartyHandler` assigns every column from the command, so a field left
 * out of the body is not "unchanged" — it arrives as null and the column is
 * cleared. Two apps edit a party (TMS and Depot) and each renders a different
 * subset of it, so anything one screen does not show is data it will destroy.
 * Requiring the fields makes that a compile error rather than a silent wipe: a
 * screen that does not edit a value has to echo the loaded party's own back.
 * </p>
 *
 * <p>
 * A caller that genuinely wants to change ONE thing should not reach for this
 * at all — that is what the narrow endpoints are for (`setPartyAssignmentCap`,
 * `setPartyRequiredOrderReferences`), and new ones should follow them.
 * </p>
 */
export interface UpdatePartyRequest {
  partyId: string;
  code: string;
  name: string;
  type: PartyTypeValue;
  /** Supplier sub-classification — only meaningful when `type=Supplier`. */
  subtype: SupplierTypeValue | null;
  status: PartyStatusValue;
  primaryAddressId: string;
  billingName: string;
  billingAddressId: string;
  billingFrequency: BillingFrequencyValue;
  paymentMethod: PaymentMethodValue;
  creditLimit: number;
  /** Null clears any customer-specific terms and falls back to the org default. */
  paymentTermDays: number | null;
  paymentTermBasis: PaymentTermBasisValue | null;
  taxNumber: string | null;
  taxSchemeCountryCode: string;
  euVatStatement: boolean;
  currencyIsoCode: string;
  accountOwnerId: string | null;
  accountSystemName: string | null;
  accountSystemCode: string | null;
  extraFields: string | null;
  /** Comma-joined ServiceTag names. Null / empty clears all tags. */
  tags: string | null;
}

/** POST `/directory/api/v1/partyContacts` body. */
export interface CreatePartyContactRequest {
  partyId: string;
  name: string;
  phoneNumber: string;
  email: string;
  notes?: string | null;
  type: ContactTypeValue;
}

/** PUT `/directory/api/v1/partyContacts` body. Id field is `contactId`. */
export interface UpdatePartyContactRequest {
  contactId: string;
  name: string;
  phoneNumber: string;
  email: string;
  notes?: string | null;
  type: ContactTypeValue;
}

/**
 * PUT `/directory/api/v1/partyDocuments` body — only metadata edits
 * are allowed via PUT (description + type). The file itself is replaced
 * by uploading a new document; the BE keeps old blobs for audit.
 */
export interface UpdatePartyDocumentRequest {
  documentId: string;
  description?: string | null;
  type: PartyDocumentTypeValue;
}

/**
 * Turn a failed blob-response request into an `Error` with a readable message.
 *
 * The shared response interceptor reads ProblemDetails off `response.data`, but
 * a request made with `responseType: 'blob'` gets its *error* body as a Blob
 * too — so that parsing is skipped and the caller is left with axios's generic
 * "Request failed with status code N". This decodes the body and prefixes the
 * status, which is the difference between a useful and a useless error toast.
 */
async function readBlobError(err: unknown): Promise<Error> {
  const response = (err as { response?: { status?: number; data?: unknown } }).response;
  const status = response?.status;
  const prefix = status ? `${status}: ` : '';

  const body = response?.data;
  if (body instanceof Blob) {
    try {
      const text = await body.text();
      const parsed = JSON.parse(text) as { detail?: string; title?: string; error?: string };
      const detail = parsed.detail || parsed.error || parsed.title;
      if (detail) return new Error(`${prefix}${detail}`);
      if (text.trim()) return new Error(`${prefix}${text.trim()}`);
    } catch {
      // Not JSON (or unreadable) — fall through to the generic message.
    }
  }

  return new Error(
    `${prefix}${err instanceof Error && err.message ? err.message : 'Request failed.'}`,
  );
}

/** Endpoints scoped to the Directory API (`/directory/api/v1/*`). */
// ── Accounting categories ──────────────────────────────────────────────────
// The analysis code beside the nominal (a QuickBooks Class, Xero Tracking
// Category, Sage Department). Hand-written until the schema is regenerated.

/** Row returned from `GET /organisationAccountingCategories`. */
export interface OrganisationAccountingCategory {
  id: string;
  organisationId: string;
  /** Ours, upper-cased by the server, unique per organisation: `CHEM`. */
  code: string;
  name: string;
  /** Optional note on what belongs in the category. */
  description: string | null;
  displayOrder: number;
  status: BaseStatusValue;
  createdUtc: string;
  updatedUtc: string | null;
}

/** POST `/organisationAccountingCategories`. */
export interface CreateOrganisationAccountingCategoryRequest {
  organisationId: string;
  code: string;
  name: string;
  description?: string | null;
  displayOrder?: number;
  status?: BaseStatusValue;
}

/** PUT `/organisationAccountingCategories` — a merge: omitted properties are kept. */
export interface UpdateOrganisationAccountingCategoryRequest {
  categoryId: string;
  code: string;
  name: string;
  description?: string | null;
  displayOrder?: number;
  status?: BaseStatusValue;
}

// ── GPS provider credentials ───────────────────────────────────────────────
// Hand-written until the schema is regenerated. The provider catalog itself
// still lives in Subsistence (`/subsistence/api/v1/gpsprovider`).

/** Row returned from `GET /gpsProviderCredentials?organisationId=`. */
export interface GpsProviderCredential {
  id: string;
  accountId: string;
  organisationId: string;
  gpsProviderId: string;
  status: BaseStatusValue;
  /** Base64 of the encrypted credential blob; never rendered in the UI. */
  encodedCredential: string | null;
  gpsProviderName: string | null;
  accountName: string | null;
  apiKey: string | null;
}

/** POST `/gpsProviderCredentials`. `gpsProviderName` is required by the BE validator. */
export interface CreateGpsProviderCredentialRequest {
  organisationId: string;
  gpsProviderId: string;
  gpsProviderName: string;
  status: BaseStatusValue;
  username: string;
  password: string;
  accountName: string | null;
  apiKey: string | null;
}

// ── Dangerous goods & waste catalogues, and the organisation's products ─────
// Hand-written until the schema is regenerated (see the note on
// `Organisation` above). Shapes mirror the Directory DTOs one-to-one.

/** ADR 2025 Table A row — every value is the table's own text, "" = blank cell. */
export interface AdrCatalogueEntry {
  /** UN + packing group + classification code, `#n` where ADR repeats it: `1993-III-F1#2`. */
  key: string;
  un: string;
  name: string;
  class: string;
  classificationCode: string;
  packingGroup: string;
  labels: string;
  specialProvisions: string;
  limitedQuantity: string;
  exceptedQuantity: string;
  tankCode: string;
  transportCategory: string;
  tunnelCode: string;
  hazardNumber: string;
  sourcePage: number;
}

/** A page of ADR rows: `total` is every match, `entries` at most `take` of them. */
export interface AdrCatalogueResult {
  edition: string;
  total: number;
  entries: AdrCatalogueEntry[];
}

/** One EU List of Waste code. `code` is as printed: `13 07 02*`. */
export interface EwcCatalogueEntry {
  code: string;
  description: string;
  chapter: string;
  /** The sub-chapter heading's code: `13 07`. */
  subChapter: string;
  hazardous: boolean;
}

export interface EwcHeading {
  code: string;
  description: string;
}

/** The whole List of Waste — chapters, sub-chapters and codes. */
export interface EwcCatalogue {
  source: string;
  count: number;
  chapters: EwcHeading[];
  subChapters: EwcHeading[];
  entries: EwcCatalogueEntry[];
}

export interface EwcCatalogueSearchResult {
  total: number;
  entries: EwcCatalogueEntry[];
}

export type ProductGradeValue = 'None' | 'Food' | 'Feed';

/**
 * A product the organisation moves. ADR / waste are facts, not a type: it is
 * dangerous goods when `adrKey` is set and waste when it has `wasteCodes` —
 * both can be. The `adr*` columns are the server's copy of the Table A row.
 */
export interface Product {
  id: string;
  organisationId: string;
  name: string;
  code: string | null;
  grade: ProductGradeValue;
  densityKgPerL: number | null;
  notes: string | null;
  status: BaseStatusValue;
  adrKey: string | null;
  adrEdition: string | null;
  adrUnNumber: string | null;
  adrProperShippingName: string | null;
  adrTechnicalName: string | null;
  /** The row carries special provision 274 — the screen should ask for a technical name. */
  adrTechnicalNameRequired: boolean;
  adrClass: string | null;
  adrClassificationCode: string | null;
  adrPackingGroup: string | null;
  adrLabels: string | null;
  adrSpecialProvisions: string | null;
  adrHazardNumber: string | null;
  adrTunnelCode: string | null;
  adrTransportCategory: string | null;
  /** The DEFAULT waste code (what a new cargo row takes); null when not waste. */
  ewcCode: string | null;
  ewcDescription: string | null;
  ewcHazardous: boolean | null;
  /** Every waste code the product may travel under, in display order. */
  wasteCodes: ProductWasteCode[];
  createdUtc: string;
  updatedUtc: string | null;
}

/**
 * One EU List of Waste code a product may travel under. A waste code depends
 * on where the waste came from, so a product can have several; each cargo row
 * declares one.
 */
export interface ProductWasteCode {
  code: string;
  description: string;
  hazardous: boolean;
  isDefault: boolean;
}

/** Filters for `GET /products`; all optional. */
export interface ProductFilters {
  search?: string;
  adr?: boolean;
  waste?: boolean;
  grade?: ProductGradeValue;
  status?: BaseStatusValue;
}

/**
 * POST `/products`. The ADR row and waste code go by KEY only — the server
 * copies class, packing group, hazard number etc. from its catalogues.
 */
export interface CreateProductRequest {
  organisationId: string;
  name: string;
  code?: string | null;
  grade?: ProductGradeValue;
  densityKgPerL?: number | null;
  notes?: string | null;
  status?: BaseStatusValue;
  adrKey?: string | null;
  adrTechnicalName?: string | null;
  /** Waste codes in display order; empty / omitted = not waste. */
  ewcCodes?: string[];
  /** One of `ewcCodes`; null = the first. */
  defaultEwcCode?: string | null;
}

/**
 * PUT `/products`. A MERGE: a property left out is kept, an explicit `null`
 * clears it. Sending `adrKey` re-copies the row; `null` clears every ADR column.
 */
export interface UpdateProductRequest {
  productId: string;
  name: string;
  code?: string | null;
  grade?: ProductGradeValue;
  densityKgPerL?: number | null;
  notes?: string | null;
  status?: BaseStatusValue;
  adrKey?: string | null;
  adrTechnicalName?: string | null;
  /** Sent: replaces the whole list (empty clears it). Omitted: kept. */
  ewcCodes?: string[];
  /** With `ewcCodes`, one of them (null = the first); alone, moves the default. */
  defaultEwcCode?: string | null;
}

/**
 * What a web app needs to start its maps, from Directory instead of a public
 * `config.json`. Either value is null when the environment has not configured
 * it — maps then degrade rather than break the screen.
 */
export interface MapsConfig {
  /** Google browser key — restricted to our sites and APIs in the Google console. */
  googleMapsApiKey: string | null;
  /** The Azure Maps ACCOUNT's client id (not a secret), sent with every token request. */
  azureMapsClientId: string | null;
}

/** A short-lived Azure Maps token (about an hour); the subscription key never reaches the browser. */
export interface AzureMapsToken {
  token: string;
  expiresOnUtc: string;
}

/**
 * One road leg to route. A point that came from Google (a Google-sourced
 * address) is sent with its place id, so Directory's route cache keys it by the
 * place and never stores Google coordinates; anything else (a pin set by hand,
 * a port from our own list, a truck position) goes by coordinates only. Use
 * `roadLegEnd()` to build the ends.
 */
export interface RoadLegQuery {
  fromLatitude: number;
  fromLongitude: number;
  fromPlaceId?: string | null;
  /** Label only — lets lane reports name the place. */
  fromAddressId?: string | null;
  toLatitude: number;
  toLongitude: number;
  toPlaceId?: string | null;
  toAddressId?: string | null;
}

/**
 * The vehicle to route as. Every field falls back to a standard artic
 * server-side — an order usually has no unit assigned while it is planned.
 */
export interface TruckProfile {
  weightKg?: number | null;
  heightMetres?: number | null;
  lengthMetres?: number | null;
  widthMetres?: number | null;
  axleWeightKg?: number | null;
  /** True for an ADR / haz load — the provider's own token is chosen server-side. */
  hazardous?: boolean;
}

/** POST `/maps/road-legs` body. */
export interface RoadLegsRequest {
  organisationId: string;
  legs: RoadLegQuery[];
  vehicle?: TruckProfile | null;
}

/**
 * A routed road leg (truck, no ferries). `minutes` / `kilometres` are null when
 * it could not be routed — the caller keeps its own estimate, marked as one.
 */
export interface RoadLeg {
  index: number;
  minutes: number | null;
  kilometres: number | null;
}

export const directoryApi = {
  /** Smoke-test endpoint that returns `{ name, version, environment }`. */
  getApiInfo: () => request.get<ApiInfo>('/directory/api/v1/info/api-info'),

  /** GET a single organisation by id. */
  /** POST create an organisation in the current account. */
  createOrganisation: (body: CreateOrganisationRequest) =>
    request.post<string>('/directory/api/v1/organisations', body),

  getOrganisation: (id: string) =>
    request.get<Organisation>(`/directory/api/v1/organisations/${id}`),

  /**
   * PUT the full organisation body. Server responds with the updated id
   * (string) wrapped in the `Result<T>` envelope; the platform `request.put`
   * helper unwraps to the bare id.
   */
  updateOrganisation: (body: UpdateOrganisationRequest) =>
    request.put<string>('/directory/api/v1/organisations', body),

  /**
   * Replace the organisation's additional-currency list atomically. The BE
   * normalises (uppercase + dedupe + drop base) and joins to comma-separated
   * for storage. Empty `codes` clears the field.
   */
  updateOrganisationAdditionalCurrencies: (
    organisationId: string,
    body: UpdateOrganisationAdditionalCurrenciesRequest,
  ) =>
    request.put<string>(
      `/directory/api/v1/organisations/${organisationId}/additional-currencies`,
      body,
    ),

  /**
   * Update the organisation's TMS order-reference-label map (used by the
   * TMS Order Details page to resolve the per-org names of
   * `Order.Reference1..4` slots + any long-tail entries). Dedicated
   * endpoint so the TMS surface can save labels without echoing the
   * entire org body back to the Directory.
   *
   * <p>
   * Body shape: `{ labelsJson: '{"Reference1":"Customer PO",...}' }` or
   * `{ labelsJson: null }` to clear. Anything other than a JSON object
   * is rejected with 422 by the BE.
   * </p>
   */
  updateOrganisationTmsOrderReferenceLabels: (
    organisationId: string,
    body: UpdateOrganisationTmsOrderReferenceLabelsRequest,
  ) =>
    request.put<string>(
      `/directory/api/v1/organisations/${organisationId}/tms-order-reference-labels`,
      body,
    ),

  /**
   * Update which Work View tabs (`/orders` — Orders, Plan, Manifest, Legs,
   * Bookings) the organisation is offered. Dedicated endpoint so the
   * settings card saves on its own rather than echoing the whole org body
   * back to the Directory.
   *
   * <p>
   * Body shape: `{ tabsJson: '{"Orders":true,"Manifest":false}' }`, or
   * `{ tabsJson: null }` to restore every tab. STRATUM SUPPORT ONLY — a
   * customer token gets 404, so callers must gate the UI on the caller's
   * role rather than letting a click discover it.
   * </p>
   */
  updateOrganisationTmsWorkViewTabs: (
    organisationId: string,
    body: UpdateOrganisationTmsWorkViewTabsRequest,
  ) =>
    request.put<string>(
      `/directory/api/v1/organisations/${organisationId}/tms-work-view-tabs`,
      body,
    ),

  /**
   * Update what the TMS Plan cockpit offers this organisation — which
   * resource pools the bottom strip carries, and whether the driver Gantt
   * exists at all.
   *
   * <p>
   * Body shape: `{ featuresJson: '{"pools":{"vehicles":false},"gantt":true}' }`,
   * or `{ featuresJson: null }` to restore everything. STRATUM SUPPORT
   * ONLY — a customer token gets 404, so callers must gate the UI on the
   * caller's role rather than letting a click discover it.
   * </p>
   */
  updateOrganisationTmsPlanFeatures: (
    organisationId: string,
    body: UpdateOrganisationTmsPlanFeaturesRequest,
  ) =>
    request.put<string>(
      `/directory/api/v1/organisations/${organisationId}/tms-plan-features`,
      body,
    ),

  /**
   * Update which TMS navigable areas the organisation uses — the sidebar
   * modules, and therefore the routes behind them.
   *
   * <p>
   * Body shape: `{ menuJson: '{"roster":false}' }`, or
   * `{ menuJson: null }` to restore the whole menu. STRATUM SUPPORT ONLY
   * — a customer token gets 404, so callers must gate the UI on the
   * caller's role rather than letting a click discover it.
   * </p>
   *
   * <p>
   * The caller should invalidate the `me` query afterwards — capabilities
   * are computed from this, so the sidebar is stale until they refetch.
   * </p>
   */
  updateOrganisationTmsMenu: (organisationId: string, body: UpdateOrganisationTmsMenuRequest) =>
    request.put<string>(`/directory/api/v1/organisations/${organisationId}/tms-menu`, body),

  /**
   * Update which TMS copy functions the organisation is offered. STRATUM
   * SUPPORT ONLY — a customer token gets 404, so callers must gate the UI on
   * the caller's role.
   */
  updateOrganisationTmsCopyFunctions: (
    organisationId: string,
    body: UpdateOrganisationTmsCopyFunctionsRequest,
  ) =>
    request.put<string>(
      `/directory/api/v1/organisations/${organisationId}/tms-copy-functions`,
      body,
    ),

  /**
   * Switch the TMS ferry (shipping) integration on or off for the
   * organisation. STRATUM SUPPORT ONLY — a customer token gets 404.
   */
  updateOrganisationTmsShippingIntegration: (
    organisationId: string,
    body: UpdateOrganisationTmsShippingIntegrationRequest,
  ) =>
    request.put<string>(
      `/directory/api/v1/organisations/${organisationId}/tms-shipping-integration`,
      body,
    ),

  /**
   * Update the organisation's Depot system settings.
   *
   * <p>
   * Body shape: `{ settingsJson: '{"products":{"restrictedWeightLimit":true}}' }`,
   * or `{ settingsJson: null }` to restore every setting as shipped. Needs
   * Organisations → Update.
   * </p>
   */
  updateOrganisationDepotSettings: (
    organisationId: string,
    body: UpdateOrganisationDepotSettingsRequest,
  ) =>
    request.put<string>(`/directory/api/v1/organisations/${organisationId}/depot-settings`, body),

  /** GET a single address by id. */
  getAddress: (id: string) => request.get<Address>(`/directory/api/v1/addresses/${id}`),

  /**
   * Hydrate a specific set of addresses in one call (POST so the id list rides
   * in the body), scoped to an organisation. Used by the TMS Bookings worklist
   * to resolve origin/destination countries for the crossing test.
   */
  getAddressesByIds: (organisationId: string, ids: string[]) =>
    request.post<Address[]>('/directory/api/v1/addresses/by-ids', { organisationId, ids }),

  /**
   * GET addresses for an organisation. The optional `opts` bag narrows
   * the result on the server: `partyId` returns only the addresses
   * linked to that party (drives the per-party "Additional Addresses"
   * card grid); `addressTypes` filters by `AddressType` enum
   * (Locations page passes `['Task','Location']` to hide
   * Primary/Billing rows). Both filters can combine — omit them all
   * for the full org-scoped list.
   */
  getAddresses: (
    organisationId: string,
    opts?: { partyId?: string; addressTypes?: AddressTypeValue[] },
  ) => {
    const qs = new URLSearchParams({ organisationId });
    if (opts?.partyId) qs.set('partyId', opts.partyId);
    if (opts?.addressTypes?.length) {
      for (const t of opts.addressTypes) qs.append('addressTypes', t);
    }
    return request.get<Address[]>(`/directory/api/v1/addresses?${qs.toString()}`);
  },

  /**
   * POST a new address. Returns the new id; the caller is responsible for
   * linking it (e.g. PUT `/organisations` with `mainOfficeAddressId`).
   */
  createAddress: (body: CreateAddressRequest) =>
    request.post<string>('/directory/api/v1/addresses', body),

  /**
   * POST a port / terminal Location address from route catalogue data.
   * Skips Google Places geocoding — the BE validator waives `fullAddress`
   * and `placeId` when `addressType` is `Location`.
   */
  createLocationAddress: (body: CreateLocationAddressRequest) =>
    request.post<string>('/directory/api/v1/addresses', body),

  /** PUT the full address body. Returns the updated address id. */
  updateAddress: (body: UpdateAddressRequest) =>
    request.put<string>('/directory/api/v1/addresses', body),

  /** Soft-delete an address. */
  deleteAddress: (id: string) =>
    request.del<unknown>(`/directory/api/v1/addresses/${id}`),

  /**
   * POST a new site. Used by the Organisation settings page to seed a
   * "Main Office" site when creating the first address for a fresh org,
   * and by the Sites tab in Users & Access for ad-hoc site creation.
   * Returns the new site id.
   */
  createSite: (body: CreateSiteRequest) => request.post<string>('/directory/api/v1/sites', body),

  /**
   * PUT an existing site. Site id travels in the body (matches the BE
   * Mediator command shape). Used by the Sites tab to rename or
   * re-address a site. Returns the (unchanged) id.
   */
  updateSite: (body: UpdateSiteRequest) => request.put<string>('/directory/api/v1/sites', body),

  /**
   * DELETE a site by id. The BE soft-deletes (Site is `ISoftDelete`),
   * so the row is recoverable but no longer surfaces in queries. The
   * handler also blocks deletion if the site is referenced (members,
   * teams, etc.) via the standard FK-conflict path.
   */
  deleteSite: (id: string) => request.del<unknown>(`/directory/api/v1/sites/${id}`),

  /** GET all numbering sequences (Invoice, CreditNote, …) for an organisation. */
  getOrganisationSequences: (organisationId: string) =>
    request.get<OrganisationSequence[]>(
      `/directory/api/v1/organisationSequences?organisationId=${organisationId}`,
    ),

  /** POST a new sequence. Returns the new sequence id. */
  createOrganisationSequence: (body: CreateOrganisationSequenceRequest) =>
    request.post<string>('/directory/api/v1/organisationSequences', body),

  /**
   * PUT an existing sequence. Returns the same id. The server ignores
   * `currentNumber` once `isLocked === true`, and rejects un-locking with a
   * 409 Conflict.
   */
  updateOrganisationSequence: (body: UpdateOrganisationSequenceRequest) =>
    request.put<string>('/directory/api/v1/organisationSequences', body),

  /**
   * Atomically allocate the next number on a sequence and return the
   * formatted reference. Used by the TMS order screen to mint an order
   * number the moment the operator picks a customer — gaps in the
   * sequence are accepted (cancelled / unsaved drafts burn a number).
   *
   * The BE handler is non-idempotent on purpose: every call allocates.
   * Callers must NOT retry naively — wrap in TanStack Query mutations
   * and surface failures rather than silently looping.
   */
  consumeOrganisationSequence: (body: ConsumeOrganisationSequenceRequest) =>
    request.post<ConsumeOrganisationSequenceResponse>(
      '/directory/api/v1/organisationSequences/consume',
      body,
    ),

  /**
   * Upload (or replace) an organisation's logo. Sends `File` as multipart
   * form-data — matches the controller's `[FromForm] UploadOrganisationLogoRequest`
   * binding. Returns the fresh blob name + a short-lived SAS URL so the
   * caller can render the new image without a second round-trip.
   */
  uploadOrganisationLogo: (id: string, file: File) =>
    request.postMultipart<UploadOrganisationLogoResponse>(
      `/directory/api/v1/organisations/${id}/logo`,
      { File: file },
    ),

  // ── Document templates ────────────────────────────────────────────────────

  /** GET an organisation's document layouts, optionally for one app only. */
  getOrganisationDocumentTemplates: (organisationId: string, app?: AppNameValue) =>
    request.get<OrganisationDocumentTemplate[]>(
      `/directory/api/v1/organisationDocumentTemplates?organisationId=${encodeURIComponent(
        organisationId,
      )}${app ? `&app=${encodeURIComponent(app)}` : ''}`,
    ),

  createOrganisationDocumentTemplate: (body: CreateOrganisationDocumentTemplateRequest) =>
    request.post<string>('/directory/api/v1/organisationDocumentTemplates', body),

  /** Rename and re-save a layout. App and document kind cannot change. */
  updateOrganisationDocumentTemplate: (body: UpdateOrganisationDocumentTemplateRequest) =>
    request.put<string>('/directory/api/v1/organisationDocumentTemplates', body),

  /** Promote a layout to the default for its app + document kind. */
  setDefaultOrganisationDocumentTemplate: (id: string) =>
    request.post<string>(`/directory/api/v1/organisationDocumentTemplates/${id}/default`, {}),

  deleteOrganisationDocumentTemplate: (id: string) =>
    request.del<string>(`/directory/api/v1/organisationDocumentTemplates/${id}`),

  /**
   * GET the organisation's logo *bytes* from the API rather than from storage.
   *
   * `organisation.logoUrl` is a storage SAS URL, which is all a browser needs
   * to *display* the image (`img` tag, download link) — but a caller that has
   * to read the bytes in JavaScript cannot use it: that is a cross-origin
   * fetch to `*.blob.core.windows.net`, which the storage account refuses
   * unless it carries a CORS rule for the app origin. This route is
   * same-origin with every other API call, so it always works.
   *
   * Used by the document builder, which embeds the logo into a generated PDF.
   * Prefer `logoUrl` for plain display — it skips our API entirely.
   */
  getOrganisationLogoContent: async (id: string) => {
    try {
      return await request.get<Blob>(`/directory/api/v1/organisations/${id}/logo/content`, {
        responseType: 'blob',
      });
    } catch (err) {
      // `responseType: 'blob'` applies to error responses too, so a failure
      // arrives with a Blob body — which the shared interceptor cannot read,
      // leaving callers with "Request failed with status code 404" and no clue
      // why. Decode it here so the message says what the server said.
      throw await readBlobError(err);
    }
  },

  /**
   * Clear an organisation's logo. Idempotent — succeeds even when no logo
   * is currently set. Server returns no body of interest, so we capture as
   * `unknown` and let the mutation discard it.
   */
  deleteOrganisationLogo: (id: string) =>
    request.del<unknown>(`/directory/api/v1/organisations/${id}/logo`),

  /**
   * GET tenants the signed-in account belongs to. Drives the TenantSwitcher
   * dropdown and seeds `setAvailableTenants(...)` so the role-resolution
   * helper in apps can pick the right organisation per tenant.
   */
  getAvailableAccounts: () => request.get<Account[]>('/directory/api/v1/accounts/available'),

  /**
   * GET every account on the platform, for the Stratum support picker.
   *
   * <p>
   * 404s for anybody whose token lacks the `super_user` claim — not 403,
   * deliberately: a 403 would confirm the route exists and is worth probing.
   * Treat a failure here as "not a support user" rather than surfacing an
   * error.
   * </p>
   */
  getSupportAccounts: () =>
    request.get<SupportAccount[]>('/directory/api/v1/support/accounts'),

  /**
   * POST enable an app for an account: ensures the subscription and grants an
   * Owner app-role in the chosen organisation.
   *
   * <p>
   * WHO receives that role depends on the caller. Self-service grants it to
   * the caller; a Stratum support caller has no membership to grant anything
   * to, so it goes to the account's owners instead — otherwise staff would
   * either gain a permanent role inside a customer's account, or leave the
   * customer with a subscription nobody can use.
   * </p>
   *
   * <p>
   * Acts on whichever account `X-Account-Key` names, so the support session
   * must already point at the customer. Idempotent.
   * </p>
   */
  provisionApp: (body: ProvisionAppRequest) =>
    request.post<ProvisionAppResponse>('/directory/api/v1/subscriptions/provision', body),

  /**
   * POST create a customer account: the account, its first organisation, and an
   * invited owner. Returns the new account id.
   *
   * <p>
   * Distinct from self-signup (`/accounts/register`), which takes the caller's
   * own password. Here the owner sets theirs from an invite — staff never
   * choose a customer's credential. 404s for anybody who is not support.
   * </p>
   */
  createAccount: (body: CreateAccountRequest) =>
    request.post<CreateAccountResponse>('/directory/api/v1/accounts', body),

  /**
   * DELETE cancel an app subscription.
   *
   * <p>
   * Switches it off and expires it — the row survives, and so does every
   * `UserAppRole` under it. Access stops because the subscription check
   * refuses the app; the roles are kept so re-enabling restores who had what,
   * which re-provisioning alone cannot (it grants only the account's owners).
   * </p>
   */
  cancelSubscription: (subscriptionId: string) =>
    request.del<string>(`/directory/api/v1/subscriptions/${subscriptionId}`),

  /** PUT rename an account (and, for support, move its domain). */
  updateAccount: (body: UpdateAccountRequest) =>
    request.put<string>('/directory/api/v1/accounts', body),

  /**
   * GET every user in the caller's account. The Settings → Users screen
   * renders straight from this — fine while accounts stay small, but a
   * dedicated `/users?organisationId=&role=` query is the long-term
   * replacement.
   */
  getAccountUsers: () => request.get<User[]>('/directory/api/v1/users'),

  /** GET a single user by id (scoped to the caller's account). */
  getUserById: (id: string) =>
    request.get<User>(`/directory/api/v1/users/${id}`),

  /**
   * POST a new user. Either creates a fresh account user or attaches an
   * existing user (matched by email) and grants the supplied platform
   * role + optional functional roles for the current app. Returns the
   * (possibly pre-existing) user id.
   */
  createUser: (body: CreateUserRequest) =>
    request.post<string>('/directory/api/v1/users', body),

  /**
   * POST a re-send of the CALLER's own verification email. No arguments: the
   * recipient is resolved from the access token server-side, so this can't be
   * aimed at another address. Rate-limited per user.
   */
  resendMyVerificationEmail: () =>
    request.post<ResendVerificationResponse>(
      '/directory/api/v1/users/me/resend-verification',
      {},
    ),

  /**
   * PUT an existing user. Updates profile fields + platform role +
   * functional roles in one shot. `functionalRoles: null` (or omitted)
   * leaves them unchanged; `[]` clears them.
   */
  updateUser: (body: UpdateUserRequest) =>
    request.put<string>('/directory/api/v1/users', body),

  /**
   * DELETE a user from the given organisation. Soft-delete on the BE —
   * leaves the underlying User record so audit trails resolve. Returns
   * 204 / no content.
   */
  deleteUser: (id: string, organisationId: string) =>
    request.del<unknown>(
      `/directory/api/v1/users/${id}?organisationId=${organisationId}`,
    ),

  /**
   * GET the functional-role catalogue. Pass `app` to filter to one app
   * (the Settings → Users picker calls with the current app); omit it
   * for a future cross-app admin screen.
   */
  /**
   * The caller's own profile for the app in the `X-App-Key` header.
   *
   * <p>
   * No id: the identity comes from the token, so it cannot be pointed at
   * anybody else. This is what the UI uses to decide what to render — the
   * alternative was listing every user in the organisation to find yourself,
   * which is a plain user reading the whole staff directory to draw a menu.
   * </p>
   */
  getMe: () => request.get<Me>('/directory/api/v1/users/me'),

  /**
   * The functional-role catalogue. Pass `organisationId` to have navigable
   * areas that organisation has switched off left out, so the picker on
   * Users & Access cannot offer a module the company does not use — a tick
   * for one would be inert anyway, because the same setting narrows the
   * capability list. Stratum support always receives the full catalogue.
   */
  getFunctionalRoles: (app?: AppNameValue, organisationId?: string) => {
    const params = new URLSearchParams();
    if (app) params.set('app', app);
    if (organisationId) params.set('organisationId', organisationId);
    const query = params.toString();
    return request.get<FunctionalRole[]>(
      query
        ? `/directory/api/v1/users/functional-roles?${query}`
        : '/directory/api/v1/users/functional-roles',
    );
  },

  /**
   * GET pending account-membership invitations for the caller. Drives
   * the post-login prompt — empty list means "nothing to do, dismiss
   * silently".
   */
  getPendingInvitations: () =>
    request.get<PendingInvitation[]>('/directory/api/v1/accountUsers/me/pending'),

  /**
   * POST accept the pending invitation identified by `accountUserId`.
   * Returns the now-Active `AccountSummary` so the FE can splice it into
   * the accounts cache without an extra round-trip. Idempotent — accepting
   * twice succeeds both times.
   */
  acceptInvitation: (accountUserId: string) =>
    request.post<AccountSummary>(
      `/directory/api/v1/accountUsers/${accountUserId}/accept`,
      {},
    ),

  // ── Teams (Settings → Users & Access → Teams tab) ─────────────────────

  /** GET teams in an organisation for an app. Members are inlined. */
  getTeams: (organisationId: string, app: AppNameValue) =>
    request.get<Team[]>(
      `/directory/api/v1/teams?organisationId=${organisationId}&app=${app}`,
    ),

  /** POST a new team. Returns the new team id. */
  createTeam: (body: CreateTeamRequest) =>
    request.post<string>('/directory/api/v1/teams', body),

  /** PUT atomic team update (name + description + site + members). Returns the same id. */
  updateTeam: (body: UpdateTeamRequest) =>
    request.put<string>('/directory/api/v1/teams', body),

  /** DELETE a team (soft-delete). */
  deleteTeam: (id: string) =>
    request.del<unknown>(`/directory/api/v1/teams/${id}`),

  // ── User-site assignment (cross-app) ─────────────────────────────────

  /** GET sites the user is currently assigned to. */
  getUserSites: (userId: string) =>
    request.get<UserSite[]>(`/directory/api/v1/users/${userId}/sites`),

  /**
   * PUT replace the user's full Site assignment list. Empty list = clear
   * all. The handler diffs current vs incoming.
   */
  replaceUserSites: (userId: string, body: ReplaceUserSitesRequest) =>
    request.put<unknown>(`/directory/api/v1/users/${userId}/sites`, body),

  /**
   * PUT replace the user's team memberships within an (org, app) scope.
   * Memberships in other scopes are left untouched.
   */
  replaceUserTeams: (userId: string, body: ReplaceUserTeamsRequest) =>
    request.put<unknown>(`/directory/api/v1/users/${userId}/teams`, body),

  // ── Sites tab (Users & Access) ───────────────────────────────────────

  /**
   * GET sites with embedded UserSite members for the Sites tab grid +
   * drawer. Distinct from `/sites` which returns the raw list without
   * memberships.
   */
  getSitesWithMembers: (organisationId: string) =>
    request.get<SiteWithMembers[]>(
      `/directory/api/v1/sites/with-members?organisationId=${organisationId}`,
    ),

  /**
   * PUT the site's full member list. Sites-direction mirror of
   * `replaceUserSites`. Empty list = clear all.
   */
  replaceSiteMembers: (siteId: string, body: ReplaceSiteMembersRequest) =>
    request.put<unknown>(`/directory/api/v1/sites/${siteId}/members`, body),

  // ── Mobile users (Settings → Users & Access → Mobile Users tab) ──────────

  /** GET mobile users in the caller's account for an (org, app). */
  getMobileUsers: (organisationId: string, app: AppNameValue = 'Safety') =>
    request.get<MobileUserSummary[]>(
      `/directory/api/v1/mobile-users?organisationId=${organisationId}&app=${app}`,
    ),

  /** POST allow-list (invite) a mobile user by phone number. */
  inviteMobileUser: (body: InviteMobileUserRequest) =>
    request.post<InviteMobileUserResponse>('/directory/api/v1/mobile-users/invite', body),

  /** PUT edit a mobile user's profile (display name + field role). */
  updateMobileUser: (id: string, body: UpdateMobileUserRequest) =>
    request.put<unknown>(`/directory/api/v1/mobile-users/${id}`, body),

  /** POST suspend a mobile user — blocks authentication until reactivated. */
  suspendMobileUser: (id: string) =>
    request.post<unknown>(`/directory/api/v1/mobile-users/${id}/suspend`, {}),

  /** POST reactivate a suspended mobile user. */
  reactivateMobileUser: (id: string) =>
    request.post<unknown>(`/directory/api/v1/mobile-users/${id}/reactivate`, {}),

  /** DELETE revoke a mobile user's grant for one app/org (leaves other apps intact). */
  /**
   * Check a username before provisioning. Pass the part after the account code
   * (`JSMITH`, not `ACME-JSMITH`); omit it to preview the next sequence number.
   */
  checkMobileUsername: (username?: string) =>
    request.get<MobileUsernameAvailability>(
      `/directory/api/v1/mobile-users/username-available${
        username ? `?username=${encodeURIComponent(username)}` : ''
      }`,
    ),

  /** Create a username/password mobile user. The password comes back once. */
  provisionMobileUsername: (body: ProvisionMobileUsernameRequest) =>
    request.post<ProvisionMobileUsernameResponse>(
      '/directory/api/v1/mobile-users/provision-username',
      body,
    ),

  /** Issue a new password. Send none to have one generated. */
  resetMobileUserPassword: (id: string, password?: string) =>
    request.post<ResetMobileUserPasswordResponse>(
      `/directory/api/v1/mobile-users/${id}/reset-password`,
      password ? { password } : {},
    ),

  removeMobileUser: (id: string, organisationId: string, app: AppNameValue = 'Safety') =>
    request.del<unknown>(
      `/directory/api/v1/mobile-users/${id}?organisationId=${organisationId}&app=${app}`,
    ),

  /** GET all bank accounts for an organisation. */
  getOrganisationBankAccounts: (organisationId: string) =>
    request.get<OrganisationBankAccount[]>(
      `/directory/api/v1/organisationBankAccounts?organisationId=${organisationId}`,
    ),

  /** POST a new bank account. Returns the new bank account id. */
  createOrganisationBankAccount: (body: CreateOrganisationBankAccountRequest) =>
    request.post<string>('/directory/api/v1/organisationBankAccounts', body),

  /** PUT an existing bank account. Returns the same id. */
  updateOrganisationBankAccount: (body: UpdateOrganisationBankAccountRequest) =>
    request.put<string>('/directory/api/v1/organisationBankAccounts', body),

  /** DELETE a bank account by id. */
  deleteOrganisationBankAccount: (id: string) =>
    request.del<unknown>(`/directory/api/v1/organisationBankAccounts/${id}`),

  /** GET all tax rates for an organisation. */
  getOrganisationTaxRates: (organisationId: string) =>
    request.get<OrganisationTaxRate[]>(
      `/directory/api/v1/organisationTaxRates?organisationId=${organisationId}`,
    ),

  /** POST a new tax rate. Returns the new id. */
  createOrganisationTaxRate: (body: CreateOrganisationTaxRateRequest) =>
    request.post<string>('/directory/api/v1/organisationTaxRates', body),

  /** PUT an existing tax rate. Returns the same id. */
  updateOrganisationTaxRate: (body: UpdateOrganisationTaxRateRequest) =>
    request.put<string>('/directory/api/v1/organisationTaxRates', body),

  /** DELETE a tax rate by id. */
  deleteOrganisationTaxRate: (id: string) =>
    request.del<unknown>(`/directory/api/v1/organisationTaxRates/${id}`),

  /**
   * Seed an organisation with statutory tax-rate templates for a country.
   * Idempotent — re-seeding never duplicates. Returns the count of newly
   * created rows.
   */
  seedOrganisationTaxRates: (body: SeedOrganisationTaxRatesRequest) =>
    request.post<number>('/directory/api/v1/organisationTaxRates/seed-defaults', body),

  /** GET the VAT treatment matrix for an organisation. */
  getOrganisationVatTreatments: (organisationId: string) =>
    request.get<VatTreatmentMatrix>(
      `/directory/api/v1/organisationVatTreatments?organisationId=${organisationId}`,
    ),

  /** PUT one line of the matrix. Answers with the whole matrix. */
  setOrganisationVatTreatment: (body: SetOrganisationVatTreatmentRequest) =>
    request.put<VatTreatmentMatrix>('/directory/api/v1/organisationVatTreatments', body),

  /**
   * Fill every UNSET line with the organisation's schema of the suggested type,
   * adding that schema from the country's defaults when it has none.
   */
  setOrganisationVatTreatmentDefaults: (organisationId: string) =>
    request.post<VatTreatmentMatrix>('/directory/api/v1/organisationVatTreatments/set-defaults', {
      organisationId,
    }),

  /** GET all nominal codes (chart of accounts) for an organisation. */
  getOrganisationNominals: (organisationId: string) =>
    request.get<OrganisationNominal[]>(
      `/directory/api/v1/organisationNominals?organisationId=${organisationId}`,
    ),

  /** POST a new nominal code. Returns the new id. */
  createOrganisationNominal: (body: CreateOrganisationNominalRequest) =>
    request.post<string>('/directory/api/v1/organisationNominals', body),

  /** PUT an existing nominal code. Returns the same id. */
  updateOrganisationNominal: (body: UpdateOrganisationNominalRequest) =>
    request.put<string>('/directory/api/v1/organisationNominals', body),

  /** DELETE a nominal code by id. */
  deleteOrganisationNominal: (id: string) =>
    request.del<unknown>(`/directory/api/v1/organisationNominals/${id}`),

  /** GET every accounting category for an organisation, in display order. */
  getOrganisationAccountingCategories: (organisationId: string) =>
    request.get<OrganisationAccountingCategory[]>(
      `/directory/api/v1/organisationAccountingCategories?organisationId=${organisationId}`,
    ),

  /** POST a new accounting category. Returns the new id. */
  createOrganisationAccountingCategory: (body: CreateOrganisationAccountingCategoryRequest) =>
    request.post<string>('/directory/api/v1/organisationAccountingCategories', body),

  /** PUT an accounting category (merge). Returns the same id. */
  updateOrganisationAccountingCategory: (body: UpdateOrganisationAccountingCategoryRequest) =>
    request.put<string>('/directory/api/v1/organisationAccountingCategories', body),

  /** DELETE (soft) an accounting category. */
  deleteOrganisationAccountingCategory: (id: string) =>
    request.del<unknown>(`/directory/api/v1/organisationAccountingCategories/${id}`),

  /** GET every GPS provider credential for an organisation. */
  getGpsProviderCredentials: (organisationId: string) =>
    request.get<GpsProviderCredential[]>(
      `/directory/api/v1/gpsProviderCredentials?organisationId=${encodeURIComponent(organisationId)}`,
    ),

  /** POST a GPS provider credential. Resolves to the created row. */
  createGpsProviderCredential: (body: CreateGpsProviderCredentialRequest) =>
    request.post<GpsProviderCredential>('/directory/api/v1/gpsProviderCredentials', body),

  /** DELETE a GPS provider credential by id. */
  deleteGpsProviderCredential: (id: string) =>
    request.del<unknown>(`/directory/api/v1/gpsProviderCredentials/${encodeURIComponent(id)}`),

  /** GET both saved layers of one grid's columns (the organisation default and mine). */
  getGridLayout: (organisationId: string, app: string, gridKey: string) => {
    const qs = new URLSearchParams({ organisationId, app, gridKey });
    return request.get<GridLayoutLayers>(`/directory/api/v1/gridLayouts?${qs.toString()}`);
  },

  /** PUT a grid's columns — my own layout, or the organisation default (who can edit the organisation). */
  saveGridLayout: (body: SaveGridLayoutRequest) =>
    request.put<boolean>('/directory/api/v1/gridLayouts', body),

  /** GET the shared views of one grid and mine, plus whether I may share. */
  getGridViews: (organisationId: string, app: string, gridKey: string) => {
    const qs = new URLSearchParams({ organisationId, app, gridKey });
    return request.get<GridViews>(`/directory/api/v1/gridViews?${qs.toString()}`);
  },

  /** POST a new named view — mine, or shared (who can edit the organisation). */
  createGridView: (body: CreateGridViewRequest) =>
    request.post<GridView>('/directory/api/v1/gridViews', body),

  /** PUT a view's name, and its columns + filters when given (omit them to only rename). */
  updateGridView: (body: UpdateGridViewRequest) =>
    request.put<GridView>('/directory/api/v1/gridViews', body),

  deleteGridView: (id: string) => request.del<boolean>(`/directory/api/v1/gridViews/${id}`),

  /** DELETE a layout, falling back to the layer beneath it. */
  resetGridLayout: (organisationId: string, app: string, gridKey: string, scope: GridLayoutScope) => {
    const qs = new URLSearchParams({ organisationId, app, gridKey, scope });
    return request.del<boolean>(`/directory/api/v1/gridLayouts?${qs.toString()}`);
  },

  /**
   * GET the static equipment catalogue. Same payload for every tenant — cache
   * aggressively on the FE (catalogue keys are append-only / never reused).
   */
  getEquipmentCatalogue: () =>
    request.get<EquipmentCatalogue>('/directory/api/v1/equipmentCatalogue'),

  /** GET every active equipment-type selection (across both classes) for an organisation. */
  getOrganisationEquipmentTypes: (organisationId: string) =>
    request.get<OrganisationEquipmentType[]>(
      `/directory/api/v1/organisationEquipmentTypes?organisationId=${organisationId}`,
    ),

  /**
   * PUT the full selection for a SINGLE class (Trailer or Rigid). The server
   * diffs against existing rows: inserts new keys, restores soft-deleted
   * keys, and soft-deletes keys removed from the set. Returns the resulting
   * active selection for the requested class only.
   */
  replaceOrganisationEquipmentTypes: (body: ReplaceOrganisationEquipmentTypesRequest) =>
    request.put<OrganisationEquipmentType[]>(
      '/directory/api/v1/organisationEquipmentTypes',
      body,
    ),

  // ── Carriers & Routes ──────────────────────────────────────────────────

  /**
   * GET the static carrier catalogue. Same payload for every tenant — cache
   * aggressively (24h staleTime); catalogue keys are append-only.
   */
  getCarrierCatalogue: () =>
    request.get<CarrierCatalogue>('/directory/api/v1/carrierCatalogue'),

  // ── Dangerous goods & waste catalogues (tenant-agnostic, read-only) ──────

  /** Search ADR Table A: a UN number or its start (`1830`, `UN18`), or name words. */
  searchAdrCatalogue: (params: { search?: string; class?: string; take?: number }) =>
    request.get<AdrCatalogueResult>('/directory/api/v1/adrCatalogue', { params }),

  /** Every Table A row for one UN number (`1993` has six). */
  getAdrEntriesByUn: (un: string) =>
    request.get<AdrCatalogueResult>(
      `/directory/api/v1/adrCatalogue/un/${encodeURIComponent(un)}`,
    ),

  /** One Table A row by key — a query parameter because keys can hold `#`. */
  getAdrEntry: (key: string) =>
    request.get<AdrCatalogueEntry>('/directory/api/v1/adrCatalogue/entry', { params: { key } }),

  /** The whole EU List of Waste (~160 KB) — cache it for the day. */
  getEwcCatalogue: () => request.get<EwcCatalogue>('/directory/api/v1/ewcCatalogue'),

  /**
   * Truck driving time and distance for road legs, through Directory's route
   * cache (Azure Maps, truck mode, ferries avoided; kept per account for up to
   * six months). A POST so pickup and drop points stay out of access logs.
   */
  getRoadLegs: (body: RoadLegsRequest) => request.post<RoadLeg[]>('/directory/api/v1/maps/road-legs', body),

  /** Map settings for the web apps (Google browser key, Azure Maps client id). */
  getMapsConfig: () => request.get<MapsConfig>('/directory/api/v1/maps/config'),

  /** A short-lived Azure Maps token — prefer `getAzureMapsToken()`, which caches it. */
  getAzureMapsTokenRaw: () => request.get<AzureMapsToken>('/directory/api/v1/maps/azure-token'),

  /** Search the List of Waste by code prefix (`13 07`) or description words. */
  searchEwcCatalogue: (params: {
    search?: string;
    hazardous?: boolean;
    chapter?: string;
    take?: number;
  }) =>
    request.get<EwcCatalogueSearchResult>('/directory/api/v1/ewcCatalogue/search', { params }),

  // ── Products ─────────────────────────────────────────────────────────────

  /** GET the organisation's products, by name. */
  getProducts: (organisationId: string, filters?: ProductFilters) =>
    request.get<Product[]>('/directory/api/v1/products', {
      params: { organisationId, ...filters },
    }),

  getProduct: (id: string) => request.get<Product>(`/directory/api/v1/products/${id}`),

  /** POST a new product. Returns the new id. */
  createProduct: (body: CreateProductRequest) =>
    request.post<string>('/directory/api/v1/products', body),

  /** PUT a product (merge — omitted properties are kept). Returns the same id. */
  updateProduct: (body: UpdateProductRequest) =>
    request.put<string>('/directory/api/v1/products', body),

  /** DELETE (soft) a product. */
  deleteProduct: (id: string) => request.del<unknown>(`/directory/api/v1/products/${id}`),

  /**
   * Switch products on or off for the organisation — the switch in the Cargo
   * Definitions header. Ordinary organisation-update permission, not support.
   */
  setOrganisationUseProducts: (organisationId: string, useProducts: boolean) =>
    request.put<string>(`/directory/api/v1/organisations/${organisationId}/use-products`, {
      useProducts,
    }),

  /**
   * How the journey planner picks an unbooked crossing — "Suggest crossings
   * by" on Central Configuration. `'Cheapest'` is refused until route rates
   * exist.
   */
  setOrganisationCrossingPreference: (
    organisationId: string,
    crossingPreference: Exclude<CrossingPreferenceValue, 'Cheapest'>,
  ) =>
    request.put<string>(`/directory/api/v1/organisations/${organisationId}/crossing-preference`, {
      crossingPreference,
    }),

  /** The organisation's CO₂ factors (Sustainability). A null factor follows the default. */
  setOrganisationEmissionFactors: (organisationId: string, body: SetEmissionFactorsRequest) =>
    request.put<EmissionFactors>(`/directory/api/v1/organisations/${organisationId}/emission-factors`, body),

  /** Star or un-star one of the organisation's routes. */
  setOrganisationRoutePreferred: (organisationRouteId: string, isPreferred: boolean) =>
    request.put<OrganisationRoute>(
      `/directory/api/v1/organisationRoutes/${organisationRouteId}/preferred`,
      { isPreferred },
    ),

  /** GET every active carrier selection for an organisation. */
  getOrganisationCarriers: (organisationId: string) =>
    request.get<OrganisationCarrier[]>(
      `/directory/api/v1/organisationCarriers?organisationId=${organisationId}`,
    ),

  /**
   * PUT the org's full carrier selection. The server diffs against existing
   * rows: inserts (auto-creating a Supplier party where there's no match),
   * restores soft-deleted rows, and soft-deletes rows whose keys are no
   * longer in the set. Returns the resulting active selection.
   */
  replaceOrganisationCarriers: (body: ReplaceOrganisationCarriersRequest) =>
    request.put<OrganisationCarrier[]>(
      '/directory/api/v1/organisationCarriers',
      body,
    ),

  /**
   * GET the static shipping-routes catalogue. Same payload for every tenant
   * — cache aggressively (24h staleTime).
   */
  getRouteCatalogue: () =>
    request.get<RouteCatalogue>('/directory/api/v1/routeCatalogue'),

  /** GET every active route selection for an organisation. */
  getOrganisationRoutes: (organisationId: string) =>
    request.get<OrganisationRoute[]>(
      `/directory/api/v1/organisationRoutes?organisationId=${organisationId}`,
    ),

  /**
   * PUT the org's full route selection — diff-style semantics
   * (insert / restore / soft-delete).
   */
  replaceOrganisationRoutes: (body: ReplaceOrganisationRoutesRequest) =>
    request.put<OrganisationRoute[]>(
      '/directory/api/v1/organisationRoutes',
      body,
    ),

  // ── Resources ──────────────────────────────────────────────────────────

  /**
   * GET the certification catalogue. Tenant-agnostic; cache aggressively
   * (24h staleTime) — the catalogue is bundled JSON, only changes on
   * deploy.
   */
  getCertificationCatalogue: () =>
    request.get<CertificationCatalogue>('/directory/api/v1/certificationCatalogue'),

  /**
   * GET every resource in the given organisation, certifications eagerly
   * included. Sorted by type (drivers first) then name.
   */
  getResources: (organisationId: string) =>
    request.get<Resource[]>(
      `/directory/api/v1/resources?organisationId=${organisationId}`,
    ),

  /** GET a single resource by id (with certifications). */
  getResource: (id: string) =>
    request.get<Resource>(`/directory/api/v1/resources/${id}`),

  /** POST a new resource. Returns the freshly-created Resource (incl. id). */
  createResource: (body: CreateResourceRequest) =>
    request.post<Resource>('/directory/api/v1/resources', body),

  /** PUT a resource update (id in body — matches the rest of the Directory API). */
  updateResource: (body: UpdateResourceRequest) =>
    request.put<Resource>('/directory/api/v1/resources', body),

  /** Soft-delete a resource. Cert rows cascade-soft-delete BE-side. */
  deleteResource: (id: string) =>
    request.del<unknown>(`/directory/api/v1/resources/${id}`),

  /**
   * Assign (or clear, with `homeSiteId: null`) the home depot on many resources
   * in one request. Resolves to the number of rows actually changed.
   */
  assignResourcesHomeSite: (body: AssignResourcesHomeSiteRequest) =>
    request.put<number>('/directory/api/v1/resources/homeSite', body),

  /**
   * Point a driver at the phone identity they sign in with, or clear it with
   * `mobileUserId: null`. This is what lets a check submitted on the app be
   * attributed back to a driver record.
   */
  linkResourceMobileUser: ({ resourceId, ...body }: LinkResourceMobileUserRequest) =>
    request.put<unknown>(`/directory/api/v1/resources/${resourceId}/mobile-user`, body),

  /**
   * Upsert a single cert on a resource. Recomputes the resource's
   * compliance roll-up. Strict applies-to: rejects e.g. "MOT on driver".
   */
  setResourceCertification: (body: SetResourceCertificationRequest) =>
    request.put<ResourceCertification>(
      '/directory/api/v1/resources/certifications',
      body,
    ),

  /** Soft-delete a single cert off a resource. */
  deleteResourceCertification: (resourceId: string, certificationId: string) =>
    request.del<unknown>(
      `/directory/api/v1/resources/${resourceId}/certifications/${certificationId}`,
    ),

  /**
   * Upload (or replace) the file attached to a cert row. Sends `File`
   * as multipart form-data — matches the BE's
   * `[FromForm] UploadResourceCertificationAttachmentRequest`. Returns
   * the cert DTO with a fresh SAS URL ready for the FE to render.
   */
  uploadResourceCertificationAttachment: (
    resourceId: string,
    certificationId: string,
    file: File,
  ) =>
    request.postMultipart<ResourceCertification>(
      `/directory/api/v1/resources/${resourceId}/certifications/${certificationId}/attachment`,
      { File: file },
    ),

  /**
   * Clear a cert's attachment metadata. The blob stays in storage in
   * case of re-attach. Idempotent — succeeds even when no attachment is
   * currently set.
   */
  deleteResourceCertificationAttachment: (resourceId: string, certificationId: string) =>
    request.del<ResourceCertification>(
      `/directory/api/v1/resources/${resourceId}/certifications/${certificationId}/attachment`,
    ),

  // ── Parties (customers + suppliers) ────────────────────────────────────

  /**
   * A record's history, newest first — its own changes and its children's.
   * Aggregate types: Party, Organisation, Site, Resource, AccountUser.
   *
   * Permission follows the record: whoever may read it may read what happened
   * to it, so no separate audit right has to be granted to the people whose job
   * is explaining the record.
   */
  getAuditTrail: (args: {
    organisationId: string;
    /** One type, several comma-separated, or omitted for everything readable. */
    aggregateType?: string;
    /**
     * One record, or omitted for every record of that type in the organisation —
     * how a settings screen asks "what changed in here lately", where there is
     * no single row to point at.
     */
    aggregateId?: string;
    take?: number;
    /**
     * Where the previous page stopped — the last entry's timestamp AND id.
     * A keyset rather than an offset: the table grows at the top, so an offset
     * would shift under the reader and repeat or skip a page.
     */
    beforeUtc?: string;
    beforeId?: string;
    /** All optional, all ANDed. Empty means no opinion, never "match nothing". */
    fromUtc?: string;
    toUtc?: string;
    actor?: string;
    action?: AuditActionValue;
    entityType?: string;
    search?: string;
  }) =>
    request.get<AuditLogEntry[]>(
      `/directory/api/v1/audit?organisationId=${encodeURIComponent(args.organisationId)}` +
        (args.aggregateType ? `&aggregateType=${encodeURIComponent(args.aggregateType)}` : '') +
        (args.aggregateId ? `&aggregateId=${encodeURIComponent(args.aggregateId)}` : '') +
        `&take=${args.take ?? 100}` +
        (args.beforeUtc && args.beforeId
          ? `&beforeUtc=${encodeURIComponent(args.beforeUtc)}&beforeId=${args.beforeId}`
          : '') +
        (args.fromUtc ? `&fromUtc=${encodeURIComponent(args.fromUtc)}` : '') +
        (args.toUtc ? `&toUtc=${encodeURIComponent(args.toUtc)}` : '') +
        (args.actor ? `&actor=${encodeURIComponent(args.actor)}` : '') +
        (args.action ? `&action=${args.action}` : '') +
        (args.entityType ? `&entityType=${encodeURIComponent(args.entityType)}` : '') +
        (args.search ? `&search=${encodeURIComponent(args.search)}` : ''),
    ),

  /**
   * GET every party in the organisation. Optional `type` filter narrows
   * to Customers (`type=Customer`) or Suppliers (`type=Supplier`); the BE
   * returns `Both` rows in either context (so a party flagged as both
   * shows on both list pages).
   */
  getParties: (organisationId: string, type?: PartyTypeValue) => {
    const qs = new URLSearchParams({ organisationId });
    if (type) qs.set('type', type);
    return request.get<Party[]>(`/directory/api/v1/parties?${qs.toString()}`);
  },

  /** GET a single party by id (basic-info shape — addresses/contacts/docs are separate calls). */
  getParty: (id: string) => request.get<Party>(`/directory/api/v1/parties/${id}`),

  /** POST a new party. Returns the new party id. */
  createParty: (body: CreatePartyRequest) =>
    request.post<string>('/directory/api/v1/parties', body),

  /** PUT a party update (id in body — matches the rest of the Directory API). */
  updateParty: (body: UpdatePartyRequest) =>
    request.put<string>('/directory/api/v1/parties', body),

  /** Soft-delete a party. Sub-entities (contacts, documents) cascade-soft-delete BE-side. */
  deleteParty: (id: string) => request.del<unknown>(`/directory/api/v1/parties/${id}`),

  /**
   * PUT a subcontractor's concurrency ceiling — how many jobs it may hold at
   * once. `null` clears it. Narrow endpoint on purpose: `updateParty` is a full
   * replace, so changing one integer through it would mean resending the whole
   * party and could clobber a concurrent edit elsewhere. Suppliers only.
   */
  setPartyAssignmentCap: (partyId: string, maxConcurrentAssignments: number | null) =>
    request.put<string>(`/directory/api/v1/parties/${partyId}/assignment-cap`, {
      maxConcurrentAssignments,
    }),

  /**
   * PUT the order reference slots a customer insists on — 1-based slot numbers,
   * `[]` or `null` to require none. The BE normalises (distinct, ascending) and
   * stores empty as null, so the column has one spelling per meaning.
   *
   * Narrow endpoint for a harder reason than the cap above: `updateParty` is a
   * full replace and the Depot party screen does not know this TMS-only field,
   * so carrying it there would let a Depot save blank a rule a TMS operator set.
   * Customers only.
   */
  /**
   * PUT this customer's document layouts — the WHOLE
   * `{ app: { kind: layoutKey } }` map, or `null` to clear every preference.
   * Merge with `utils/documentLayoutPrefs` before calling: one app must never
   * drop another's branch.
   *
   * Off the party PUT on purpose. That PUT is a replace shared by two apps, and
   * a map keyed by app has no business travelling on it — an echo of a value
   * read minutes ago is a lost update, however complete the body.
   */
  setPartyDocumentLayouts: (partyId: string, preferredDocumentLayoutsJson: string | null) =>
    request.put<string>(`/directory/api/v1/parties/${partyId}/document-layouts`, {
      preferredDocumentLayoutsJson,
    }),

  setPartyRequiredOrderReferences: (partyId: string, slots: number[] | null) =>
    request.put<string>(
      `/directory/api/v1/parties/${partyId}/tms-required-order-references`,
      { slots },
    ),

  // ── Subcontractor trailers ─────────────────────────────────────────────

  /** GET every subcontractor-supplied trailer in the organisation, ordered by unit name. */
  getSubcontractorTrailers: (organisationId: string) =>
    request.get<SubcontractorTrailer[]>(
      `/directory/api/v1/subcontractorTrailers?organisationId=${encodeURIComponent(organisationId)}`,
    ),

  /** POST a new subcontractor trailer. Returns the new id. */
  createSubcontractorTrailer: (body: CreateSubcontractorTrailerRequest) =>
    request.post<string>('/directory/api/v1/subcontractorTrailers', body),

  /** PUT an existing subcontractor trailer (id in body, as elsewhere in this API). */
  updateSubcontractorTrailer: (body: UpdateSubcontractorTrailerRequest) =>
    request.put<string>('/directory/api/v1/subcontractorTrailers', body),

  /**
   * Soft-delete a subcontractor trailer. Currently the only way a unit leaves
   * the planning pool — nothing can retire a spent drop trailer automatically
   * until jobs record which unit they used.
   */
  deleteSubcontractorTrailer: (id: string) =>
    request.del<unknown>(`/directory/api/v1/subcontractorTrailers/${id}`),

  // ── Party contacts ─────────────────────────────────────────────────────

  /** GET every contact attached to a party. */
  getPartyContacts: (partyId: string) =>
    request.get<PartyContact[]>(`/directory/api/v1/partyContacts?partyId=${partyId}`),

  /** GET a single contact by id. Rarely used directly; the list call covers most reads. */
  getPartyContact: (id: string) =>
    request.get<PartyContact>(`/directory/api/v1/partyContacts/${id}`),

  /** POST a new contact. Returns the new contact id. */
  createPartyContact: (body: CreatePartyContactRequest) =>
    request.post<string>('/directory/api/v1/partyContacts', body),

  /** PUT a contact update (id in body — matches the rest of the Directory API). */
  updatePartyContact: (body: UpdatePartyContactRequest) =>
    request.put<string>('/directory/api/v1/partyContacts', body),

  /** Soft-delete a contact. */
  deletePartyContact: (id: string) =>
    request.del<unknown>(`/directory/api/v1/partyContacts/${id}`),

  // ── Party documents ────────────────────────────────────────────────────

  /** GET every document attached to a party. */
  getPartyDocuments: (partyId: string) =>
    request.get<PartyDocument[]>(`/directory/api/v1/partyDocuments?partyId=${partyId}`),

  /** GET a single document by id (downloadUrl is a fresh SAS on every read). */
  getPartyDocument: (id: string) =>
    request.get<PartyDocument>(`/directory/api/v1/partyDocuments/${id}`),

  /**
   * Upload a new document. Sends `File` + metadata as multipart form-data
   * — matches the BE's `[FromForm] UploadPartyDocumentRequest` binding.
   * Returns the new document id.
   */
  uploadPartyDocument: (
    partyId: string,
    file: File,
    description: string | null,
    type: PartyDocumentTypeValue,
  ) =>
    request.postMultipart<string>('/directory/api/v1/partyDocuments', {
      PartyId: partyId,
      File: file,
      Description: description ?? '',
      Type: type,
    }),

  /**
   * PUT a document metadata edit (description + type only — the file
   * itself is replaced by uploading a new document; old blobs are kept
   * for audit).
   */
  updatePartyDocument: (body: UpdatePartyDocumentRequest) =>
    request.put<string>('/directory/api/v1/partyDocuments', body),

  /** Soft-delete a document. Blob stays in storage in case of restore. */
  deletePartyDocument: (id: string) =>
    request.del<unknown>(`/directory/api/v1/partyDocuments/${id}`),

  // ── Driver Roster ───────────────────────────────────────────────────────--

  /** GET the roster grid for an org + month (1-based month). */
  getRoster: (organisationId: string, year: number, month: number) =>
    request.get<RosterMonthDto>(
      `/directory/api/v1/roster?organisationId=${encodeURIComponent(organisationId)}&year=${year}&month=${month}`,
    ),

  /** GET the flat list of rostered drivers (config + name) for the Drivers grid. */
  getDriverRosterConfigs: (organisationId: string) =>
    request.get<RosterDriverRow[]>(
      `/directory/api/v1/roster/drivers?organisationId=${encodeURIComponent(organisationId)}`,
    ),

  /** Create or update a driver's roster config. */
  upsertDriverRosterConfig: (body: UpsertDriverRosterConfigRequest) =>
    request.put<DriverRosterConfigDto>('/directory/api/v1/roster/config', body),

  /** GET a single driver's roster blocks overlapping a month (1-based). */
  getRosterBlocks: (
    organisationId: string,
    driverResourceId: string,
    year: number,
    month: number,
  ) =>
    request.get<DriverRosterBlockDto[]>(
      `/directory/api/v1/roster/blocks?organisationId=${encodeURIComponent(organisationId)}&driverResourceId=${encodeURIComponent(driverResourceId)}&year=${year}&month=${month}`,
    ),

  /** Add a working-day / leave block to a driver. */
  createRosterBlock: (body: CreateRosterBlockRequest) =>
    request.post<DriverRosterBlockDto>('/directory/api/v1/roster/blocks', body),

  /** Edit an existing roster block (dates, kind, start time, notes). */
  updateRosterBlock: (body: UpdateRosterBlockRequest) =>
    request.put<DriverRosterBlockDto>('/directory/api/v1/roster/blocks', body),

  /** Remove a roster block. */
  deleteRosterBlock: (id: string) =>
    request.del<unknown>(`/directory/api/v1/roster/blocks/${id}`),

  /** GET all roster groups for an org (for the picker + management). */
  getRosterGroups: (organisationId: string) =>
    request.get<RosterGroupDto[]>(
      `/directory/api/v1/rosterGroups?organisationId=${encodeURIComponent(organisationId)}`,
    ),

  /** Create a roster group. */
  createRosterGroup: (body: CreateRosterGroupRequest) =>
    request.post<RosterGroupDto>('/directory/api/v1/rosterGroups', body),

  /** Update a roster group. */
  updateRosterGroup: (body: UpdateRosterGroupRequest) =>
    request.put<RosterGroupDto>('/directory/api/v1/rosterGroups', body),

  /** Soft-delete a roster group. */
  deleteRosterGroup: (id: string) =>
    request.del<unknown>(`/directory/api/v1/rosterGroups/${id}`),

  /** The organisation's connected Microsoft 365 / Google mailboxes. */
  getOrganisationEmailConnections: (organisationId: string) =>
    request.get<OrganisationEmailConnections>(
      `/directory/api/v1/OrganisationEmailConnections?organisationId=${encodeURIComponent(organisationId)}`,
    ),

  /**
   * The Microsoft / Google page to send the browser to. With `adminConsent`,
   * the Microsoft link an organisation's IT admin opens to approve the app.
   */
  startEmailConnection: (body: StartEmailConnectionRequest) =>
    request.post<StartEmailConnectionResponse>(
      '/directory/api/v1/OrganisationEmailConnections/oauth/start',
      body,
    ),

  /** From address, name, "Use for" and default. Whole-object: send every field. */
  updateEmailConnection: (body: UpdateEmailConnectionRequest) =>
    request.put<EmailConnection>('/directory/api/v1/OrganisationEmailConnections', body),

  /** Sends a test to the signed-in user; success makes the connection Active. */
  sendEmailConnectionTest: (id: string) =>
    request.post<EmailConnection>(`/directory/api/v1/OrganisationEmailConnections/${id}/test`, {}),

  /** Disconnects: the stored sign-in is wiped at once. */
  deleteEmailConnection: (id: string) =>
    request.del<unknown>(`/directory/api/v1/OrganisationEmailConnections/${id}`),
};

// ────────────────────────────────────────────────────────────────────────────
// Organisation email connections (hand-written — wire format matches
// My.Directory OrganisationEmailConnectionsController; reconcile on the next
// OpenAPI regen).
// ────────────────────────────────────────────────────────────────────────────

export type EmailProvider = 'Microsoft' | 'Google';

/** Only Active sends. Pending = signed in, not yet proven by a test email. */
export type EmailConnectionStatus = 'Pending' | 'Active' | 'NeedsReconnect';

/** What a connected mailbox sends — its "Use for". */
export type EmailDocumentKind = 'Invoices' | 'Quotes' | 'Pods' | 'Operations';

export const EMAIL_DOCUMENT_KINDS: readonly EmailDocumentKind[] = [
  'Invoices',
  'Quotes',
  'Pods',
  'Operations',
];

export interface EmailConnection {
  id: string;
  organisationId: string;
  provider: EmailProvider;
  sendFromAddress: string;
  sendFromName: string | null;
  /** The account that signed in; differs from sendFromAddress for a shared mailbox. */
  signedInAddress: string;
  status: EmailConnectionStatus;
  usedFor: EmailDocumentKind[];
  isDefault: boolean;
  verifiedUtc: string | null;
  lastSentUtc: string | null;
  lastFailureUtc: string | null;
  lastFailureReason: string | null;
  createdUtc: string;
  createdBy: string;
}

export interface OrganisationEmailConnections {
  connections: EmailConnection[];
  /** Whether I may connect, change or remove them (account owners/admins). */
  canManage: boolean;
  /** Connecting Microsoft mailboxes is set up in this environment. */
  microsoftAvailable: boolean;
  googleAvailable: boolean;
}

export interface StartEmailConnectionRequest {
  organisationId: string;
  provider: EmailProvider;
  /** The app page to come back to — must be one of our apps. */
  returnUrl: string;
  /** Reconnect this connection instead of adding one. */
  connectionId?: string | null;
  adminConsent?: boolean;
}

export interface StartEmailConnectionResponse {
  authorizationUrl: string;
  expiresUtc: string;
}

export interface UpdateEmailConnectionRequest {
  id: string;
  sendFromAddress: string;
  sendFromName: string | null;
  usedFor: EmailDocumentKind[];
  isDefault: boolean;
}

/**
 * Which connected mailbox a document of `kind` will go from — the same rule
 * Directory applies when it sends (`EmailSenderChoice`): the mailbox whose
 * "Use for" claims the kind, else the default; mailboxes not yet proven by a
 * test email (Pending) take over nothing. `null` = Stratum's own sender.
 *
 * For SHOWING the sender before a send; Directory decides for real.
 */
export function resolveEmailSender(
  connections: readonly EmailConnection[],
  kind: EmailDocumentKind,
): EmailConnection | null {
  const usable = connections.filter((c) => c.status !== 'Pending');
  return usable.find((c) => c.usedFor.includes(kind)) ?? usable.find((c) => c.isDefault) ?? null;
}

/**
 * The `emailConnection` query value Directory sends the browser back with
 * after Microsoft / Google sign-in.
 */
export type EmailConnectionReturn =
  | 'connected'
  | 'admin_consent_required'
  | 'admin_consent_granted'
  | 'denied'
  | 'expired'
  | 'failed';

// ────────────────────────────────────────────────────────────────────────────
// Driver Roster DTOs (hand-written — wire format matches My.Directory roster
// endpoints; reconcile on the next OpenAPI regen).
// ────────────────────────────────────────────────────────────────────────────

export type RosterScheduleType = 'FullTime' | 'Casual';
export type RosterWorkingPattern =
  | 'MondayToFriday'
  | 'MondayToSaturday'
  | 'TuesdayToSaturday'
  | 'SundayToThursday'
  | 'TwoWeekRotation';
export type RosterBlockKind = 'Working' | 'Holidays' | 'RestPeriod' | 'SickDay' | 'OtherLeave';
export type RosterJobType = 'Trunk' | 'MultiDrop' | 'Shunter' | 'Ferry' | 'General';
/** Per-day grid status code. */
export type RosterDayStatus = 'W' | 'D/O' | 'H' | 'R' | 'S' | 'O';

export interface RosterDriverDto {
  resourceId: string;
  name: string;
  /** HH:mm, or null. */
  start: string | null;
  location: string | null;
  casual: boolean;
  /** Resource compliance roll-up — Red / Yellow / Green. */
  complianceIndicator: string;
  /** One status per day of the month. */
  days: RosterDayStatus[];
}

export interface RosterGroupRowsDto {
  groupId: string | null;
  groupName: string;
  sortOrder: number;
  drivers: RosterDriverDto[];
}

export interface RosterMonthDto {
  year: number;
  month: number;
  days: number;
  groups: RosterGroupRowsDto[];
}

export interface RosterGroupDto {
  id: string;
  name: string;
  sortOrder: number;
}

/** One row in the Drivers management grid — a driver's config flattened with name + group name. */
export interface RosterDriverRow {
  resourceId: string;
  name: string;
  rosterGroupId: string | null;
  groupName: string | null;
  homeDepotSiteId: string | null;
  startLocation: string | null;
  defaultStartTime: string | null;
  scheduleType: RosterScheduleType;
  workingPattern: RosterWorkingPattern;
  phaseEvenWeekStart: boolean;
  jobType: RosterJobType | null;
}

export interface DriverRosterConfigDto {
  id: string;
  resourceId: string;
  rosterGroupId: string | null;
  homeDepotSiteId: string | null;
  startLocation: string | null;
  /** HH:mm, or null. */
  defaultStartTime: string | null;
  scheduleType: RosterScheduleType;
  workingPattern: RosterWorkingPattern;
  phaseEvenWeekStart: boolean;
  jobType: RosterJobType | null;
}

export interface DriverRosterBlockDto {
  id: string;
  driverResourceId: string;
  /** yyyy-MM-dd. */
  fromDate: string;
  toDate: string;
  kind: RosterBlockKind;
  /** HH:mm, or null. */
  startTime: string | null;
  notes: string | null;
}

/** PUT `/roster/config` body. Times are `HH:mm:ss`, dates `yyyy-MM-dd`. */
export interface UpsertDriverRosterConfigRequest {
  organisationId: string;
  resourceId: string;
  rosterGroupId?: string | null;
  homeDepotSiteId?: string | null;
  startLocation?: string | null;
  defaultStartTime?: string | null;
  scheduleType: RosterScheduleType;
  workingPattern: RosterWorkingPattern;
  phaseEvenWeekStart: boolean;
  jobType?: RosterJobType | null;
}

/** POST `/roster/blocks` body. */
export interface CreateRosterBlockRequest {
  organisationId: string;
  driverResourceId: string;
  fromDate: string;
  toDate: string;
  kind: RosterBlockKind;
  startTime?: string | null;
  notes?: string | null;
}

/** PUT `/roster/blocks` body. Edits an existing block; driver + org are fixed. */
export interface UpdateRosterBlockRequest {
  id: string;
  fromDate: string;
  toDate: string;
  kind: RosterBlockKind;
  startTime?: string | null;
  notes?: string | null;
}

export interface CreateRosterGroupRequest {
  organisationId: string;
  name: string;
  sortOrder: number;
}

export interface UpdateRosterGroupRequest {
  rosterGroupId: string;
  name: string;
  sortOrder: number;
}

/** Whose grid layout a request is about. */
export type GridLayoutScope = 'Mine' | 'Organisation';

/** Both saved layers of one grid (`GET gridLayouts`). */
export interface GridLayoutLayers {
  /** The organisation default; null = the columns as the screen's code has them. */
  organisationLayoutJson: string | null;
  /** The signed-in user's own; null = the organisation default. */
  userLayoutJson: string | null;
  /** Whether this user may save or clear the organisation default. */
  canEditOrganisationDefault: boolean;
}

export interface SaveGridLayoutRequest {
  organisationId: string;
  /** `tms`, `depot`, … */
  app: string;
  /** `orders`, `planJobQueue`, … */
  gridKey: string;
  scope: GridLayoutScope;
  /** `{ "v": 1, "columns": [{ "id", "visible" }] }` — see `gridLayout.ts`. */
  layoutJson: string;
}

/** A named view of a grid: columns plus the screen's filters and sort. */
export interface GridView {
  id: string;
  name: string;
  /** Listed for the whole organisation. */
  shared: boolean;
  /** The columns, in the grid-layout format. */
  layoutJson: string;
  /** The screen's filters and sort, as a JSON object only that screen reads. */
  stateJson: string;
  /** Whether I may update, rename or delete it. */
  canEdit: boolean;
  updatedUtc: string | null;
}

export interface GridViews {
  views: GridView[];
  /** Whether I may create shared views. */
  canShare: boolean;
}

export interface CreateGridViewRequest {
  organisationId: string;
  app: string;
  gridKey: string;
  name: string;
  shared: boolean;
  layoutJson: string;
  stateJson: string;
}

export interface UpdateGridViewRequest {
  viewId: string;
  name: string;
  /** Omit to keep the view's columns (a rename). */
  layoutJson?: string;
  /** Omit to keep the view's filters (a rename). */
  stateJson?: string;
}
